import { paymentStatus } from "./paymentStatus";
import { type PayerProofV1, parsePayoutAddress, payerProofMessage, payoutClaimMessage } from "@common/platform/payers";
import type { EntryPaymentIntentOwnerViewV1 } from "@common/platform/payments";
import { isTokenRoomId, type RoomEntryPolicy, type RoomId } from "@common/platform/rooms";
import { roomStateOf, type RoomStateView } from "@common/platform/roomState";
import { ROUND_LIMITS, type RoundSummary } from "@common/platform/rounds";
import { formatTokenAmount, type StorePayInstruction } from "@common/platform/store";
import { feePercentText } from "@common/platform/vaults";
import { guestName } from "@common/platform/wallet";
import { isSkyName } from "@common/utils/mapWeather";
import { checkNickname } from "@common/utils/nickname";
import { encodeFunctionData, getAddress, isAddress, parseAbi } from "viem/utils";
import { type ChainReads, chainReads } from "./chainReads";
import { GameConsole } from "./console/gameConsole";
import { noteCurrentPing, wayInOffer } from "./utils/entrances";
import { announceSignedIn, SIGNED_OUT_TEXT } from "./utils/signedIn";
import { sentences, t, tr } from "./utils/translations/phrases";
import { translationsReady } from "./utils/translations/translations";

interface WalletSession {
	readonly address: `0x${string}`;
	/** signed in with the wallet itself: every room */
	readonly verified: boolean;
	/** a guest (no wallet): the free room and the Training Range, named Guest-1234 */
	readonly guest?: boolean;
	readonly expiresAt: number;
}

interface AuthNonce {
	readonly nonce: string;
	readonly expiresAt: number;
	readonly domain: string;
	readonly termsVersion: string;
}

interface Eip1193Provider {
	request<T = unknown>(request: {
		readonly method: string;
		readonly params?: readonly unknown[] | object;
	}): Promise<T>;
}

interface Registration {
	readonly roundId: string;
	readonly roomId: RoomId;
	readonly state:
		| "registered"
		| "ready"
		| "admitted"
		| "playing"
		| "eliminated"
		| "disqualified"
		| "removed";
	/** out of the running round already, in this place: they can watch it, not play in it again (it ends their turn) */
	readonly knockedOut?: number;
}

/** in the running round and still in it (not knocked out): Enter battle takes them (back) in */
const inRoundToPlay = (): boolean =>
	registration?.state === "playing" && registration.knockedOut === undefined;

interface SessionResponse {
	readonly session: WalletSession;
	readonly registration: Registration | null;
	readonly developmentTestMode: boolean;
	readonly activeDevelopmentTest: {
		readonly gameID: number;
		readonly botCount: number;
	} | null;
	/** the wallet's latest paid entry (the last hour): what became of it, after a reload too */
	readonly paidEntry?: EntryPaymentIntentOwnerViewV1;
	/** that entry is in a round its payer couldn't play in: what becomes of it is an admin's decision */
	readonly paidEntryHeld?: boolean;
	/** once: what became of this player's queue place (it went to a wallet signed in on the same connection) */
	readonly notice?: string;
	/** a guest's wins in the USDG rooms (the last week): where each pot goes, or that it waits for the guest to say */
	readonly guestWins?: readonly GuestWin[];
	/** a wallet's own wins in the USDG rooms (the last day): what became of each pot (it goes to that wallet) */
	readonly walletWins?: readonly WalletWin[];
	/** the server's clock when it answered (ms): signed messages are dated by it, not by this device's */
	readonly serverNow?: number;
}

/** a guest's win in a USDG room (see /api/payouts/mine) */
interface GuestWin {
	readonly roundId: string;
	readonly roomId: RoomId;
	readonly endedAt?: number;
	/** under review, final, paid by the vault, or refunded instead (an admin's decision: every entry went back) */
	readonly state: "provisional" | "finalized" | "paid" | "refunded";
	readonly potAtomic?: string;
	/** what the winner gets of the pot: the pot less the vault's betting fee (absent: not known yet, never guessed) */
	readonly prizeAtomic?: string;
	/** the vault's betting fee, in basis points (500 = 5%; 0: none, the whole pot) */
	readonly winnerFeeBps?: number;
	/** where the pot goes (absent: it waits for the guest to say) */
	readonly payTo?: string;
	/** the guest chose it (otherwise: the wallet that paid its entry, once the wait is over) */
	readonly chosen: boolean;
	/** the wallet that paid the winning entry: it signs where the pot goes */
	readonly payer?: `0x${string}`;
	readonly claimUntil?: number;
	/** where it goes can still be chosen or changed (under review; or final, not chosen yet, before the wait is over) */
	readonly open?: boolean;
	/** under review: when the result becomes final (absent: it waits for an admin's decision) */
	readonly finalizesAt?: number;
}

/** a win of a player signed in with their wallet, in a USDG room: its pot goes to that wallet (nothing to choose) */
interface WalletWin {
	readonly roundId: string;
	readonly roomId: RoomId;
	readonly endedAt?: number;
	/** under review, final (the pot is sent then), paid by the vault, or refunded instead (an admin's decision) */
	readonly state: "provisional" | "finalized" | "paid" | "refunded";
	readonly potAtomic?: string;
	/** what the winner gets of the pot: the pot less the vault's betting fee (absent: not known yet, never guessed) */
	readonly prizeAtomic?: string;
	/** the vault's betting fee, in basis points (500 = 5%; 0: none, the whole pot) */
	readonly winnerFeeBps?: number;
	/** under review: when the result becomes final (absent: it waits for an admin's decision) */
	readonly finalizesAt?: number;
}

/**
 * when a result under review becomes final, said for a player: a round nobody reported is final a minute or so after it
 * ends, a reported one when an admin decides (or after the review window)
 */
const finalWhen = (finalizesAt: number | undefined): string => {
	if (finalizesAt === undefined) return t("once an admin has reviewed the round");
	const left = finalizesAt - serverNow();
	return left < 90_000 ? t("in a moment") : t("at <time>", { time: new Date(finalizesAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) });
};

/** how the USDG rooms can be paid on this server (see /api/entry/methods) */
interface EntryMethods {
	/**
	 * the USDG coin and its vault; `winnerFeeBps`: what the vault takes from a won pot for the betting treasury (500 =
	 * 5%: the winner gets the rest), absent while the server doesn't know it
	 */
	readonly usdg: { readonly chainId: number; readonly tokenAddress: `0x${string}`; readonly tokenDecimals: number; readonly escrowAddress?: `0x${string}`; readonly rpcUrl?: string; readonly winnerFeeBps?: number } | null;
	/** guests can pay for the USDG rooms (they're open, and paid entry is on) */
	readonly guestsPay: boolean;
	/** how long a guest winner's pot waits for it to say where it goes */
	readonly guestPayoutWaitHours: number;
}

interface DevelopmentAdmission {
	readonly gameID: number;
	readonly ticket: string;
	readonly botCount: number;
}

interface RoundStatus {
	readonly activeRound?: {
		readonly state:
			| "waiting"
			| "ready_check"
			| "countdown"
			| "in_progress"
			| "provisional"
			| "finalized"
			| "cancelled";
	};
	readonly nextRound: {
		readonly id?: string;
		readonly state:
			| "waiting"
			| "ready_check"
			| "countdown"
			| "in_progress"
			| "provisional"
			| "finalized"
			| "cancelled";
		readonly registeredCount: number;
		readonly readyCount: number;
		/** server time (ms) when the ready check closes */
		readonly readyDeadline?: number;
		/** server time (ms) when the round starts */
		readonly startsAt?: number;
	};
	readonly rooms: readonly {
		readonly definition: {
			readonly id: RoomId;
			readonly displayName: string;
			readonly entryPolicy: RoomEntryPolicy;
			/** where paid entries go: burned (Token room), or the winner's pot (USDG rooms) */
			readonly entryFundsPolicy?: { readonly kind: "burn" | "winner_pot" };
			readonly enabled: boolean;
			/** how many players a round of this room needs, and takes */
			readonly limits?: { readonly minPlayers: number; readonly maxPlayers: number };
		};
		readonly availability: "available" | "coming_soon";
		readonly entryLabel: string;
		readonly activeRound?: RoundSummary;
		readonly nextRound?: RoundSummary;
		/** an admin paused registration in this room */
		readonly registrationPaused?: boolean;
	}[];
	/** how long a paid round waits for enough players before it's cancelled and everyone refunded (set while paid rooms are open) */
	readonly paidRoundWaitMs?: number;
	/** how long a finished round's result waits before it's final: without reports, and with them */
	readonly reviewRules?: { readonly cleanGraceMs: number; readonly reviewMs: number };
	/**
	 * the Token room's prize while the fee pool pays: the share of the pool the winner of the round taking players gets
	 * (70%: `amountOnly` when the share has reached the most a winner gets, never said, so the line says the amount
	 * without the percent) of what `roundsBefore` leave, the rounds paid first (being played, under review, or final and
	 * not paid yet: 0.83). Not the pool itself (0.91: with it, the most a winner gets could be worked out)
	 */
	readonly feePool?: {
		readonly winnerAtomic: string;
		readonly tokenDecimals: number;
		readonly symbol: string;
		readonly winnerPercent: number;
		readonly amountOnly?: boolean;
		readonly roundsBefore?: number;
	};
}

let session: WalletSession | undefined;
let registration: Registration | null = null;
let roundStatus: RoundStatus | undefined;
let developmentTestMode = false;
let activeDevelopmentTest: SessionResponse["activeDevelopmentTest"] = null;
let developmentAdmission: DevelopmentAdmission | undefined;
let selectedRoomId: RoomId = "free";
let entryIntent: EntryPaymentIntentOwnerViewV1 | undefined;
let _refreshTimer: ReturnType<typeof setInterval> | undefined;
/** the session's last paid entry was taken up once, on coming back to the page (a room picked since stays picked) */
let paidEntryAdopted = false;
/** the paid entry (its request's ID) the server says is in a round its payer couldn't play in */
let heldEntryId: string | undefined;
/** the player's last payment request, as the server says (made in this tab or another) */
let latestPaidEntry: EntryPaymentIntentOwnerViewV1 | undefined;
/**
 * A payment this browser sent whose request the game server no longer has (a restart lost it): it isn't a seat, and
 * it's refunded to the wallet that paid it, by the settlement bot (when it's automatic: a round with no game record,
 * about two hours after its first payment) or by an admin (Refund in the Control Room; manual is the bot's default).
 * Said in the menu for the player who paid it, on this browser, for a day; paying that room again asks first (it used
 * to show "Pay to join" with nothing about the payment)
 */
interface LostPayment {
	readonly intentId: string;
	readonly roomId: RoomId;
	readonly player: string;
	readonly payer: string;
	readonly amountAtomic: string;
	readonly tokenDecimals: number;
	readonly noticedAt: number;
	/** the player said yes to paying that room again since: not asked again (it's still said until it's back) */
	readonly acknowledged?: boolean;
}
const LOST_PAYMENTS_KEY = "trenchio.lostPayments";
/** said for this long after it was noticed (the bot's refund usually comes within about two hours; an admin's may take longer) */
const LOST_PAYMENT_SHOWN_MS = 24 * 60 * 60_000;
let lostPaymentList: LostPayment[] | undefined;
const isLostPayment = (value: unknown): value is LostPayment => {
	const kept = value as Partial<Record<keyof LostPayment, unknown>> | null;
	return (
		typeof kept === "object" &&
		kept !== null &&
		typeof kept.intentId === "string" &&
		typeof kept.roomId === "string" &&
		typeof kept.player === "string" &&
		typeof kept.payer === "string" &&
		typeof kept.amountAtomic === "string" &&
		/^\d{1,40}$/u.test(kept.amountAtomic) &&
		typeof kept.tokenDecimals === "number" &&
		Number.isInteger(kept.tokenDecimals) &&
		kept.tokenDecimals >= 0 &&
		kept.tokenDecimals <= 36 &&
		typeof kept.noticedAt === "number" &&
		(kept.acknowledged === undefined || typeof kept.acknowledged === "boolean")
	);
};
const lostPayments = (): LostPayment[] => {
	if (!lostPaymentList) {
		try {
			const kept = JSON.parse(localStorage.getItem(LOST_PAYMENTS_KEY) ?? "[]") as unknown;
			lostPaymentList = Array.isArray(kept) ? kept.filter(isLostPayment) : [];
		} catch {
			lostPaymentList = [];
		}
	}
	lostPaymentList = lostPaymentList.filter(({ noticedAt }) => Date.now() - noticedAt < LOST_PAYMENT_SHOWN_MS);
	return lostPaymentList;
};
const keepLostPayments = (list: LostPayment[]): void => {
	lostPaymentList = list;
	try {
		if (list.length) localStorage.setItem(LOST_PAYMENTS_KEY, JSON.stringify(list));
		else localStorage.removeItem(LOST_PAYMENTS_KEY);
	} catch {
		/* storage unavailable: said for this page's life */
	}
};
const noteLostPayment = (payment: LostPayment): void =>
	keepLostPayments([...lostPayments().filter(({ intentId }) => intentId !== payment.intentId), payment].slice(-4));
/** this player's payment for a room that the game lost, still on its way back to the wallet that paid it */
const lostPaymentFor = (roomId: RoomId): LostPayment | undefined =>
	session ? [...lostPayments()].reverse().find(({ player, roomId: room }) => player === session?.address && room === roomId) : undefined;
/**
 * bumped by every sign-in, sign-out and change of place made here: a session check sent before one is stale when it
 * comes back (it showed the old wallet signed in again for a moment, and took up its paid entry once more)
 */
let authEpoch = 0;
/** session checks in order: an older one finishing late never overwrites a newer one */
let refreshSeq = 0;
let appliedSeq = 0;
/** since when the session check has been failing (the server out of reach a moment, an update going in): the last known state stays */
let sessionCheckFailingSince: number | undefined;
/**
 * when the main button's press still on its way was made (one press, one request). It holds the button until it's
 * done, however long: a payment can take a while (a wallet app asking twice, the chain confirming), and a second press
 * then started a second payment of the same request (a second approval and entry, the second refused by the vault after its fee). A wallet
 * that never answers is waited for with a hint (answer it, or reload the page: nothing is paid twice)
 */
let actionInFlightSince: number | undefined;
const actionBusy = (): boolean => actionInFlightSince !== undefined;
/**
 * what the press under way said last ("Allowing the exact amount…", "Entry submitted…"): shown until it's done, instead
 * of the line worked out from the payment request (the server hadn't seen the payment yet, and it said "Ready to pay"
 * after the money had gone)
 */
let flowStatus: string | undefined;
/** when it said that (a step that has taken long gets a hint) */
let flowStatusSince = 0;
/** the wallet whose payment is going through right now: what's shown of it stays until it's done, whatever else changes */
let paymentFor: string | undefined;
/** each wallet sign-in try's number: Cancel moves on to the next, and a late answer to an earlier try is ignored */
let walletAttempt = 0;
/** how the USDG rooms can be paid here (asked of the server at the start, and now and then) */
let entryMethods: EntryMethods | undefined;
/** this guest's wins in the USDG rooms (from the session check) */
let guestWins: readonly GuestWin[] = [];
let walletWins: readonly WalletWin[] = [];
/** the paying wallet's signed proof for this player, kept while it's valid (asked once per payment, not per request) */
let payerProof: (PayerProofV1 & { readonly player: string; readonly validUntil: number }) | undefined;
/**
 * how far this device's clock is from the game server's (ms): the server's time is Date.now() + clockOffset. Signed
 * messages are dated by it (a phone's clock a few minutes off had its payer proofs and payout choices refused), and
 * payment requests' times (the server's) are compared with it. From the session checks that came back fastest.
 */
let clockOffset = 0;
const clockSamples: { readonly offset: number; readonly roundTrip: number }[] = [];
const serverNow = (): number => Date.now() + clockOffset;
const noteServerClock = (serverTime: number | undefined, askedAt: number, answeredAt: number): void => {
	const roundTrip = answeredAt - askedAt;
	if (typeof serverTime !== "number" || !Number.isFinite(serverTime) || roundTrip < 0 || roundTrip > 5_000) return;
	clockSamples.push({ offset: serverTime - (askedAt + answeredAt) / 2, roundTrip });
	if (clockSamples.length > 10) clockSamples.shift();
	clockOffset = clockSamples.reduce((best, sample) => (sample.roundTrip < best.roundTrip ? sample : best)).offset;
};
/** a USDG room (paid from any wallet, a guest's too); the Token room is paid from a signed-in player's own tokens */
const isStakeRoom = (roomId: RoomId | undefined): boolean =>
	roomId === "stake_10" || roomId === "stake_100" || roomId === "stake_300";

/** an amount of the room's coin, for players: "10" or "10.50" (USDG has 6 decimals) */
const coinText = (atomic: bigint | string, decimals: number): string => {
	const value = BigInt(atomic);
	const scale = 10n ** BigInt(decimals);
	const cents = (value * 100n + scale - 1n) / scale;
	const whole = cents / 100n;
	const rest = cents % 100n;
	return rest === 0n ? whole.toLocaleString("en-US") : `${whole.toLocaleString("en-US")}.${rest.toString().padStart(2, "0")}`;
};

/** an amount someone holds or is sent, to the last unit: "10", "10.123456" (never rounded up) */
const exactCoinText = (atomic: bigint | string, decimals: number): string => {
	const value = BigInt(atomic);
	const scale = 10n ** BigInt(decimals);
	const whole = (value / scale).toLocaleString("en-US");
	const rest = (value % scale).toString().padStart(decimals, "0").replace(/0+$/u, "");
	return rest ? `${whole}.${rest}` : whole;
};

const refreshEntryMethods = async (): Promise<void> => {
	try {
		entryMethods = await request<EntryMethods>("/entry/methods", { signal: checkSignal() });
	} catch {
		/* the last answer stays */
	}
};

let chainReader: { readonly key: string; readonly reads: ChainReads } | undefined;
/** the chain, read for this page (balances, receipts): its public address, or the game server when the page can't reach it */
const chainReadsNow = (): ChainReads | undefined => {
	const usdg = entryMethods?.usdg;
	if (!usdg) return undefined;
	const key = `${usdg.rpcUrl ?? ""}|${usdg.tokenAddress}|${usdg.escrowAddress ?? ""}`;
	if (chainReader?.key !== key)
		chainReader = { key, reads: chainReads({ rpcUrl: usdg.rpcUrl, usdgToken: usdg.tokenAddress, usdgVault: usdg.escrowAddress, apiBase: API_URL }) };
	return chainReader.reads;
};

/** a flow for this player, still for them after waiting (signed in as someone else meanwhile: stopped before anything more is sent) */
const stillPlayer = (owner: string | undefined): void => {
	if (owner === undefined || session?.address !== owner)
		throw new Error(t("Signed in as someone else meanwhile (here or in another tab): stopped, nothing more was sent"));
};
/** how this player is named in the messages its wallet signs (a guest: its Guest-1234, as the server checks) */
const nameInMessages = (): string =>
	session?.guest ? guestName(session.address) : (myDisplayName() ?? "Player");

/** whether a room takes paid entry (by its entry policy; a room the page doesn't know yet: any but the free room) */
const roomIsPaid = (roomId: RoomId | undefined): boolean => {
	const room = roundStatus?.rooms.find(({ definition }) => definition.id === roomId)?.definition;
	return room ? room.entryPolicy.kind !== "free" : roomId !== undefined && roomId !== "free";
};

/** a wait, for players: "60 seconds", "2 minutes", "30 minutes", "2 hours" */
const waitText = (ms: number): string => {
	if (ms < 120_000) {
		const seconds = Math.round(ms / 1000);
		return seconds === 1 ? t("<n> second", { n: seconds }) : t("<n> seconds", { n: seconds });
	}
	// (two minutes at least here, and two hours at least: never one)
	const minutes = Math.round(ms / 60_000);
	return minutes >= 120 && minutes % 60 === 0 ? t("<n> hours", { n: minutes / 60 }) : t("<n> minutes", { n: minutes });
};

/** the paid-room rule, in one line: a paid seat is kept until the round is played, or cancelled with a refund */
const paidSeatRule = (): string =>
	t("If fewer than <min> players have joined <wait> after the first one, the round is cancelled and everyone gets their entry back.", {
		min: ROUND_LIMITS.minPlayers,
		wait: waitText(roundStatus?.paidRoundWaitMs ?? 30 * 60_000),
	});

/**
 * the menu's Rules: their figures as the server has them (the paid rounds' wait, the USDG vault's fee, the review
 * times), not as they were written in (the dialog said "within 30 minutes" and "less 5%" whatever the server was set to).
 * Until the server has said, the dialog's own words stand
 */
const rulesFigures = (): void => {
	const put = (id: string, text: string | undefined): void => {
		const element = document.getElementById(id);
		if (element && text !== undefined && element.textContent !== text) element.textContent = text;
	};
	const wait = roundStatus?.paidRoundWaitMs;
	put("rules-paid-wait", typeof wait === "number" && wait > 0 ? waitText(wait) : undefined);
	const bps = entryMethods?.usdg?.winnerFeeBps;
	put(
		"rules-usdg-prize",
		bps === undefined || !Number.isInteger(bps) || bps < 0 || bps > 1_000
			? undefined
			: bps > 0
				? t("the winner gets the pot, less <fee> that keeps the game running (the vault sends both in the same transaction)", { fee: feePercentText(bps) })
				: t("the winner gets the whole pot"),
	);
	const review = roundStatus?.reviewRules;
	const clean = Number(review?.cleanGraceMs), reported = Number(review?.reviewMs);
	put(
		"rules-review",
		Number.isFinite(clean) && Number.isFinite(reported) && clean >= 0 && reported > 0
			? clean > 0
				? t("Results are final <wait> after a round nobody reported; a reported round is reviewed for <review>", { wait: waitText(clean), review: waitText(reported) })
				: t("Results are final once a round nobody reported ends; a reported round is reviewed for <review>", { review: waitText(reported) })
			: undefined,
	);
};
/**
 * a payment request that's over: a new one is made for the next payment. So is one that can't be paid any more (a
 * minute after it ran out), whatever it was last marked: the server says what became of it
 */
const finishedIntent = (intent: EntryPaymentIntentOwnerViewV1): boolean =>
	["expired", "cancelled", "refund_pending", "refunded", "consumed"].includes(intent.state) ||
	serverNow() > intent.expiresAt + 60_000;
/**
 * A payment seen on the blockchain whose payer isn't in its round yet (being confirmed, or confirmed and about to be
 * used), while that round still takes players: whatever its request's time, it's checked in the vault (Check my
 * payment), never paid again; the server puts its payer in once it's confirmed. Once the round has started without
 * them, or the server says so, it's a payment in a round they couldn't play in (an admin decides)
 */
const paymentGoingIn = (
	intent: EntryPaymentIntentOwnerViewV1 | undefined,
): intent is EntryPaymentIntentOwnerViewV1 & { readonly state: "confirming" | "confirmed" } =>
	intent !== undefined &&
	(intent.state === "confirming" || intent.state === "confirmed") &&
	intent.id !== heldEntryId &&
	roundStatus?.rooms.find(({ definition }) => definition.id === intent.roomId)?.nextRound?.id === intent.roundId;
/**
 * A payment in a round its payer couldn't play in: the server says so, or it was seen on the blockchain and its round
 * has started (or gone) without them. An admin decides what happens to it; the player can pay for the next round
 */
const strayEntry = (intent: EntryPaymentIntentOwnerViewV1 | undefined): boolean =>
	intent !== undefined &&
	(intent.id === heldEntryId || (roundStatus !== undefined && (intent.state === "confirming" || intent.state === "confirmed") && !paymentGoingIn(intent)));
/** a payment still going through for its room (the menu stays on that room meanwhile): one that can still be paid, or being confirmed */
const paymentUnderWay = (intent: EntryPaymentIntentOwnerViewV1 | undefined): boolean =>
	intent !== undefined && ((!finishedIntent(intent) && !strayEntry(intent)) || paymentGoingIn(intent));

const tokenAbi = parseAbi([
	"function approve(address spender, uint256 amount) returns (bool)",
]);
/**
 * the store's payment: the coin's own burn of the price (0.95: its supply goes down for good), or, the store says so (a
 * coin without one; a server from before 0.95), a plain transfer of it to the dead address (0.92)
 */
const storeBurnAbi = parseAbi(["function burn(uint256 amount)"]);
const storeTokenAbi = parseAbi(["function transfer(address to, uint256 amount) returns (bool)"]);
const allowanceAbi = parseAbi(["function allowance(address owner, address spender) view returns (uint256)"]);
const escrowAbi = parseAbi([
	"function enter((bytes32 intentId,address payer,bytes32 roomId,bytes32 roundId,uint64 roomConfigVersion,uint256 amount,uint64 deadline,uint8 fundsPolicy) authorization, bytes signature)",
]);

// ---------- nickname and the "Next round" panel
let myName: string | null = null;
let nameLoadedFor: string | undefined;
interface LobbyState {
	readonly state: string;
	readonly startsAt: number | null;
	readonly count: number;
	readonly min: number;
	readonly max: number;
	readonly players: readonly string[];
	readonly development: boolean;
	readonly activeRound: {
		readonly state: string;
		readonly startedAt: number | null;
		readonly players: number;
		readonly practice: boolean;
	} | null;
}
let lobby: LobbyState | undefined;
/** the room `lobby` is for (the panel said "Next round" without saying whose) */
let lobbyRoomId: RoomId | undefined;

/** what this player is called here: their name, a guest's Guest-1234 (never its made-up address), or the short address */
const myDisplayName = (): string | undefined =>
	session ? (myName ?? (session.guest ? guestName(session.address) : shorten(session.address))) : undefined;

const renderLobby = (): void => {
	const timer = document.getElementById("lobby-timer");
	const sub = document.getElementById("lobby-sub");
	const fill = document.getElementById("lobby-bar-fill");
	const minMark = document.getElementById("lobby-bar-min");
	const count = document.getElementById("lobby-count");
	const list = document.getElementById("lobby-players");
	const live = document.getElementById("lobby-live");
	if (!timer || !sub || !fill || !minMark || !count || !list || !live || !lobby)
		return;
	const roomName = document.getElementById("lobby-room");
	if (roomName) {
		const room = roundStatus?.rooms.find(({ definition }) => definition.id === lobbyRoomId);
		const text = room ? tr(shortRoomName(room)) : "";
		if (roomName.textContent !== text) roomName.textContent = text;
		roomName.hidden = !text;
	}
	const counting = lobby.state === "countdown" && lobby.startsAt !== null;
	const left = counting ? secondsUntil(lobby.startsAt ?? undefined) : 0;
	// the wait ran out but the round hasn't started: it starts once the server has a game free for it
	const waitingForGame =
		counting &&
		!lobby.activeRound &&
		serverNow() - (lobby.startsAt ?? serverNow()) > 3_000;
	timer.classList.toggle("waiting", !counting || waitingForGame);
	timer.textContent =
		counting && !waitingForGame
			? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`
			: t("Waiting");
	sub.textContent = counting
		? lobby.activeRound
			? t("Starts as soon as the current round ends")
			: waitingForGame
				? t("Starts as soon as a game is free on the server")
				: lobby.development
					? t("until the round starts \u00B7 bots fill empty seats")
					: t("until the round starts")
		: lobby.count === 0
			? t("Nobody has registered yet")
			: lobby.min - lobby.count === 1
				? t("<n> more player to start the timer", { n: lobby.min - lobby.count })
				: t("<n> more players to start the timer", { n: lobby.min - lobby.count });
	fill.style.width = `${Math.min(100, (100 * lobby.count) / lobby.max)}%`;
	minMark.style.left = `calc(${(100 * lobby.min) / lobby.max}% - 1px)`;
	minMark.hidden = lobby.min <= 1;
	count.textContent =
		lobby.min > 1
			? t("<count> / <max> players \u00B7 <min> to start", { count: lobby.count, max: lobby.max, min: lobby.min })
			: t("<count> / <max> players", { count: lobby.count, max: lobby.max });
	const me = myDisplayName();
	list.replaceChildren(
		...(lobby.players.length
			? lobby.players.map((name) => {
					const li = document.createElement("li");
					li.textContent = name;
					if (name === me) li.className = "me";
					return li;
				})
			: [
					Object.assign(document.createElement("li"), {
						className: "empty",
						textContent: t("Be the first to register."),
					}),
				]),
	);
	live.hidden = !lobby.activeRound;
	if (lobby.activeRound) {
		const since = lobby.activeRound.startedAt
			? elapsed(lobby.activeRound.startedAt)
			: "";
		const players = lobby.activeRound.players;
		const round = lobby.activeRound.practice
			? players === 1
				? t("Round in progress \u00B7 <n> player + bots", { n: players })
				: t("Round in progress \u00B7 <n> players + bots", { n: players })
			: players === 1
				? t("Round in progress \u00B7 <n> player", { n: players })
				: t("Round in progress \u00B7 <n> players", { n: players });
		live.textContent = `${round}${since}`;
	}
};

const refreshLobby = async (): Promise<void> => {
	const roomId = selectedRoomId;
	try {
		const answer = await request<LobbyState>(
			`/public/lobby?room=${encodeURIComponent(roomId)}`,
			{ signal: checkSignal() },
		);
		// another room picked meanwhile: this answer is the last room's (its own is on its way)
		if (roomId !== selectedRoomId) return;
		lobby = answer;
		lobbyRoomId = roomId;
	} catch {
		/* keep the last state */
	}
	renderLobby();
};

const nameHint = (): string => t("Shown in game, in the kill feed and on the leaderboards.");
/** you've typed in the nickname field: the menu's refreshes leave it alone until you save, cancel or sign out */
let nameEdited = false;
/**
 * (1.05) the nickname field, in the kit card, is open: the pencil by the name opened it (or the wallet has no nickname
 * yet). Folded away otherwise, the card showing the kit
 */
let nameEditorOpen = false;
let nameEditorCloses: ReturnType<typeof setTimeout> | undefined;
const showNameEditor = (open: boolean): void => {
	nameEditorOpen = open;
	clearTimeout(nameEditorCloses);
	document.getElementById("name-editor")?.classList.toggle("open", open || (session !== undefined && !myName));
};
/** the nickname field lives in the Customize box: the pencil by the address jumps to it */
const openNameEditor = (): void => {
	const input = document.getElementById(
		"name-input",
	) as HTMLInputElement | null;
	if (!input || !session) return;
	if (!input.value) input.value = myName ?? "";
	showNameEditor(true);
	input.scrollIntoView({ block: "nearest" });
	input.focus();
	input.select();
};
/** Cancel: back to the saved name */
const closeNameEditor = (): void => {
	const input = document.getElementById(
		"name-input",
	) as HTMLInputElement | null;
	const hint = document.getElementById("name-hint");
	nameEdited = false;
	if (input) {
		input.value = myName ?? "";
		input.blur();
	}
	if (hint) {
		hint.textContent = nameHint();
		hint.classList.remove("bad", "good");
	}
	showNameEditor(false);
};
/**
 * a name's problem in the player's language: checkNickname's words (the server's too), the two with a number in them
 * ("At least 3 characters") as phrases of their own
 */
const nicknameProblem = (error: string): string => {
	const length = /^At (least|most) (\d+) characters$/u.exec(error);
	if (!length) return tr(error);
	const n = Number(length[2]);
	return length[1] === "least" ? t("At least <n> characters", { n }) : t("At most <n> characters", { n });
};

const saveName = async (): Promise<void> => {
	const input = document.getElementById(
		"name-input",
	) as HTMLInputElement | null;
	const hint = document.getElementById("name-hint");
	if (!input || !hint) return;
	const check = checkNickname(input.value);
	if (!check.ok) {
		hint.textContent = nicknameProblem(check.error);
		hint.classList.remove("good");
		hint.classList.add("bad");
		return;
	}
	try {
		const saved = await request<{ name: string }>("/profile/name", {
			method: "PUT",
			body: JSON.stringify({ name: check.name }),
		});
		myName = saved.name;
		nameEdited = false;
		input.value = saved.name;
		hint.textContent = t("Saved: you're \"<name>\" from your next match.", { name: saved.name });
		hint.classList.remove("bad");
		hint.classList.add("good");
		// (folded away again once that's been read, unless it's being changed again)
		clearTimeout(nameEditorCloses);
		nameEditorCloses = setTimeout(() => {
			if (!nameEdited && document.activeElement !== input) showNameEditor(false);
		}, 5_000);
		render(t("You're now \"<name>\". It shows from your next match.", { name: saved.name }));
		void refreshLobby();
	} catch (error) {
		hint.textContent =
			error instanceof Error ? tr(error.message) : t("Could not save the name");
		hint.classList.remove("good");
		hint.classList.add("bad");
	}
};

// ---------- running games (server dropdown): watch any of them, join test battles
interface GameListing {
	readonly gameId: number;
	readonly kind: "public" | "test" | "leftover";
	readonly practice: boolean;
	/** a paid round: watched on the watching page only, never from inside its match (1.04.3) */
	readonly paid?: boolean;
	readonly owner?: string;
	readonly alive: number;
	readonly humans: number;
	readonly startedAt?: number;
	readonly joinable: boolean;
}
let runningGames: GameListing[] = [];
let listingDevelopment = false;

/** how long a round has been on, by the server's clock (its start is the server's time: a device's clock off by a minute was a minute off) */
const elapsed = (since?: number): string => {
	if (!since) return "";
	const s = Math.max(0, Math.floor((serverNow() - since) / 1000));
	return ` \u00B7 ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const renderGameList = (): void => {
	const list = document.getElementById("server-list");
	if (!list) return;
	list
		.querySelectorAll(".game-list-head, .game-list-item, .game-list-empty")
		.forEach((el) => el.remove());
	const head = document.createElement("li");
	head.className = "game-list-head";
	head.textContent = listingDevelopment ? t("RUNNING GAMES") : t("LIVE ROUND");
	list.appendChild(head);
	if (!runningGames.length) {
		const empty = document.createElement("li");
		empty.className = "game-list-empty";
		empty.textContent = listingDevelopment
			? t("No games running. Start a test battle or register for a round.")
			: t("No round in progress right now.");
		list.appendChild(empty);
		return;
	}
	for (const game of runningGames) {
		const item = document.createElement("li");
		item.className = "game-list-item";
		const title = document.createElement("span");
		title.className = "gl-title";
		// the list gives the owner's address shortened (it's public): compared with ours, shortened the same way
		const mine = Boolean(
			game.owner &&
				session &&
				game.owner.toLowerCase() === shorten(session.address).toLowerCase(),
		);
		title.textContent =
			game.kind === "public"
				? t("Public round")
				: game.kind === "leftover"
					? t("Bots finishing a round")
					: mine
						? t("Your test battle")
						: t("Test battle");
		if (game.kind === "public" && game.practice) {
			const tag = document.createElement("span");
			tag.className = "gl-tag";
			tag.textContent = t("with bots");
			// (a space before it: the title's words may read right to left)
			title.append(" ", tag);
		}
		const meta = document.createElement("span");
		meta.className = "gl-meta";
		const by =
			game.kind === "test" && !mine && game.owner
				? `${shorten(game.owner)} \u00B7 `
				: "";
		const people = game.humans
			? ` \u00B7 ${game.humans === 1 ? t("<n> person", { n: game.humans }) : t("<n> people", { n: game.humans })}`
			: "";
		meta.textContent = `${by}${t("<n> alive", { n: game.alive })}${people}${elapsed(game.startedAt)}`;
		const actions = document.createElement("span");
		actions.className = "gl-actions";
		const watch = document.createElement("button");
		watch.type = "button";
		watch.className = "gl-btn";
		watch.textContent = t("Watch");
		watch.dataset.act = "watch";
		watch.dataset.game = String(game.gameId);
		actions.appendChild(watch);
		if (game.joinable) {
			const join = document.createElement("button");
			join.type = "button";
			join.className = "gl-btn join";
			join.textContent = t("Join");
			join.dataset.act = "join";
			join.dataset.game = String(game.gameId);
			actions.appendChild(join);
		}
		item.append(title, actions, meta);
		list.appendChild(item);
	}
};

const refreshGames = async (): Promise<void> => {
	try {
		const body = await request<{ development: boolean; games: GameListing[] }>(
			"/games",
			{ signal: checkSignal() },
		);
		runningGames = body.games;
		listingDevelopment = body.development;
	} catch {
		runningGames = [];
	}
	renderGameList();
	render();
};

/** leave whatever match is on screen (a test battle, or a game you are watching) */
const leaveCurrentGame = async (): Promise<void> => {
	try {
		const { Game } = await import("./game");
		if (Game.gameStarted) await Game.endGame();
	} catch {
		/* not in a game */
	}
};

const openGame = async (
	act: "watch" | "join",
	gameId: number,
): Promise<void> => {
	if (!session) throw new Error(t("Play as a guest or sign in with your wallet first"));
	// in development (the live test server runs in it) every running game is watched from inside its match, 15 seconds
	// behind live, a public round too (pressing Watch on one used to open the watching page, which hides the map
	// during a live round). In production a public round is watched on the watching page
	// (1.04.3) a paid round is always watched there: nobody watches one from inside its match
	if (act === "watch" && (!listingDevelopment || runningGames.some((game) => game.gameId === gameId && game.paid))) {
		location.href = "./stats/";
		return;
	}
	await leaveCurrentGame();
	developmentAdmission = await request<DevelopmentAdmission>(
		act === "watch" ? "/dev/watch" : "/dev/join",
		{
			method: "POST",
			body: JSON.stringify({ gameId }),
		},
	);
	render(
		act === "watch"
			? t("Opening the spectator view\u2026")
			: t("Joining the test battle\u2026"),
	);
	document.getElementById("btn-play-solo")?.click();
};

// ---------- lobby niceties: how this device last signed in, ready-check chime, auto-enter, ping
/**
 * how this device last signed in ("wallet" or "guest"): signed out again, the menu puts that button first. Only a
 * hint for the layout (nothing about who they are is kept)
 */
const LAST_SIGN_IN_KEY = "trenches_last_sign_in";
const lastSignIn = (): string | null => {
	try {
		return localStorage.getItem(LAST_SIGN_IN_KEY);
	} catch {
		return null; // storage unavailable
	}
};
/** a wallet sign-in waiting on the wallet app (connect, then sign): the menu says where to look meanwhile */
let walletPending: { readonly since: number } | undefined;
/** which part of the wallet sign-in is waiting: the wallet sharing its account, the signature, or the server's check */
let walletStep: "connect" | "sign" | "check" = "connect";
const AUTO_JOIN_DELAY_MS = 3_000;
/** rounds for which the auto-enter already fired or was cancelled */
const autoJoinHandled = new Set<string>();
let autoJoin: { readonly roundId: string; readonly at: number } | undefined;
let lastNextState: string | undefined;
let _uiTimer: ReturnType<typeof setInterval> | undefined;
let _titleTimer: ReturnType<typeof setInterval> | undefined;
const baseTitle = document.title;

const secondsUntil = (t?: number): number =>
	t === undefined ? 0 : Math.max(0, Math.ceil((t - serverNow()) / 1000));

/** the next round of the room this player is registered in (else the one picked): each room has its own */
const myNextRound = (): RoundStatus["nextRound"] | undefined => {
	const roomId = registration?.roomId ?? selectedRoomId;
	return roundStatus?.rooms.find(({ definition }) => definition.id === roomId)?.nextRound ?? roundStatus?.nextRound;
};

/** true while a match (test battle or spectating) is on screen; imported lazily to keep the modules independent */
const inGame = async (): Promise<boolean> => {
	try {
		return (await import("./game")).Game.gameStarted;
	} catch {
		return false;
	}
};

/** a small banner that is visible over the game too (the lobby itself is hidden while you play) */
let _toastTimer: ReturnType<typeof setTimeout> | undefined;
const toast = (text: string, ms = 5000): void => {
	let el = document.getElementById("lobby-toast");
	if (!el) {
		el = document.createElement("div");
		el.id = "lobby-toast";
		el.style.cssText =
			"position:fixed;left:50%;top:calc(env(safe-area-inset-top,0px) + 70px);transform:translateX(-50%);z-index:9999;" +
			"background:rgba(20,20,22,.92);color:#fff;padding:10px 18px;border-radius:12px;border:2px solid #f0c05a;font:600 16px Inter,system-ui,sans-serif;" +
			"box-shadow:0 6px 20px rgba(0,0,0,.5);pointer-events:none;transition:opacity .25s;max-width:90vw;text-align:center";
		document.body.appendChild(el);
	}
	el.textContent = text;
	el.style.opacity = "1";
	if (_toastTimer) clearTimeout(_toastTimer);
	_toastTimer = setTimeout(() => {
		if (el) el.style.opacity = "0";
	}, ms);
};

let audioCtx: AudioContext | undefined;
/** browsers only allow sound after a click; any click in the lobby unlocks it */
const unlockAudio = (): void => {
	try {
		audioCtx ??= new AudioContext();
		if (audioCtx.state === "suspended") void audioCtx.resume();
	} catch {
		/* no audio available */
	}
};
/** a short rising three-note chime for the ready check */
const playReadyChime = (): void => {
	const context = audioCtx;
	if (!context) return;
	let volume = 0.22;
	try {
		volume *=
			Number(GameConsole.getBuiltInCVar("cv_master_volume")) *
			Number(GameConsole.getBuiltInCVar("cv_sfx_volume"));
	} catch {
		/* settings not ready: default volume */
	}
	if (!(volume > 0)) return;
	const t0 = context.currentTime + 0.02;
	[
		[784, 0],
		[988, 0.14],
		[1319, 0.28],
	].forEach(([freq, delay]) => {
		const osc = context.createOscillator();
		const gain = context.createGain();
		osc.type = "triangle";
		osc.frequency.value = freq;
		gain.gain.setValueAtTime(0.0001, t0 + delay);
		gain.gain.exponentialRampToValueAtTime(volume, t0 + delay + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, t0 + delay + 0.45);
		osc.connect(gain).connect(context.destination);
		osc.start(t0 + delay);
		osc.stop(t0 + delay + 0.5);
	});
};

/** your round has started (you're admitted): a short bugle call */
const playAdmittedCall = (): void => {
	const context = audioCtx;
	if (!context) return;
	let volume = 0.2;
	try {
		volume *=
			Number(GameConsole.getBuiltInCVar("cv_master_volume")) *
			Number(GameConsole.getBuiltInCVar("cv_sfx_volume"));
	} catch {
		/* settings not ready: default volume */
	}
	if (!(volume > 0)) return;
	const t0 = context.currentTime + 0.02;
	[
		[392, 0, 0.16],
		[523, 0.18, 0.16],
		[659, 0.36, 0.16],
		[784, 0.54, 0.5],
	].forEach(([freq, delay, length]) => {
		const osc = context.createOscillator();
		const gain = context.createGain();
		osc.type = "sawtooth";
		osc.frequency.value = freq;
		const filter = context.createBiquadFilter();
		filter.type = "lowpass";
		filter.frequency.value = 1800;
		gain.gain.setValueAtTime(0.0001, t0 + delay);
		gain.gain.exponentialRampToValueAtTime(volume, t0 + delay + 0.03);
		gain.gain.exponentialRampToValueAtTime(0.0001, t0 + delay + length);
		osc.connect(filter).connect(gain).connect(context.destination);
		osc.start(t0 + delay);
		osc.stop(t0 + delay + length + 0.05);
	});
};

/** your round started while this tab is in the background: the tab title blinks until you come back */
let _admittedTimer: ReturnType<typeof setInterval> | undefined;
const updateAdmittedAlert = (active: boolean): void => {
	if (active && !_admittedTimer) {
		let on = false;
		_admittedTimer = setInterval(() => {
			on = !on;
			document.title = on ? `\u2694 ${t("YOUR ROUND HAS STARTED")}` : baseTitle;
		}, 700);
	} else if (!active && _admittedTimer) {
		clearInterval(_admittedTimer);
		_admittedTimer = undefined;
		document.title = baseTitle;
	}
};

/** while a ready check waits for you in a background tab, the tab title blinks */
const updateTitleAlert = (active: boolean): void => {
	if (active && !_titleTimer) {
		let on = false;
		_titleTimer = setInterval(() => {
			on = !on;
			const left = secondsUntil(myNextRound()?.startsAt);
			document.title = on ? `\u26A0 ${t("ROUND STARTS \u00B7 <s>s", { s: left })}` : baseTitle;
		}, 800);
	} else if (!active && _titleTimer) {
		clearInterval(_titleTimer);
		_titleTimer = undefined;
		document.title = baseTitle;
	}
};

const measurePing = async (): Promise<void> => {
	const label = document.getElementById("server-ping");
	if (!label) return;
	const url = new URL(`${API_URL}/serverInfo`, location.href).href;
	// room for these measurements' timings (nothing else on the page reads them; the buffer holds only 250)
	performance.clearResourceTimings?.();
	let best = Infinity;
	for (let i = 0; i < 2; i++) {
		const started = performance.now();
		try {
			// the whole answer read, so the browser has recorded its timing by the time it's looked up
			await (await fetch(url, { cache: "no-store", signal: checkSignal() })).arrayBuffer();
			const wall = performance.now() - started;
			// the network's own time when the browser recorded it (a busy page, still loading, can't inflate that)
			const entry = (performance.getEntriesByName(url, "resource") as PerformanceResourceTiming[])
				.filter((timing) => timing.startTime >= started - 1)
				.pop();
			const network =
				entry && entry.requestStart > 0 && entry.responseStart >= entry.requestStart
					? entry.responseStart - entry.requestStart
					: undefined;
			best = Math.min(best, network ?? wall);
		} catch {
			/* offline */
		}
	}
	if (!Number.isFinite(best)) {
		label.textContent = "";
		return;
	}
	const ms = Math.round(best);
	label.textContent = `${ms} ms`;
	label.classList.toggle("slow", ms >= 80 && ms < 150);
	label.classList.toggle("bad", ms >= 150);
	// an entrance (a relay near far-away players) that's clearly faster for this player, timed now and then
	await noteCurrentPing(ms);
	const hint = document.getElementById("entrance-hint") as HTMLAnchorElement | null;
	const offer = wayInOffer();
	if (hint) {
		hint.hidden = offer === undefined;
		if (offer) {
			hint.href = `${offer.url}/`;
			hint.textContent =
				offer.name === "main"
					? t("Faster for you: the main address (<ping> ms instead of <current> ms)", { ping: offer.ping, current: offer.current })
					: t("Faster for you: the <entrance> entrance (<ping> ms instead of <current> ms)", { ping: offer.ping, current: offer.current, entrance: tr(offer.name) });
		}
	}
};

/**
 * for a check the menu repeats (the session, the rooms, the games, the ping): its answer waited for 10 seconds at most.
 * A hung request held its place, and the checks after it piled up behind it
 */
const checkSignal = (): AbortSignal | undefined =>
	typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(10_000) : undefined;

/** a check the menu repeats, never two at once: one still out when the next is due is waited for, not sent again */
const oneAtATime = (check: () => Promise<void>): (() => void) => {
	let out = false;
	return () => {
		if (out) return;
		out = true;
		void check().finally(() => {
			out = false;
		});
	};
};

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
	const response = await fetch(`${API_URL}${path}`, {
		credentials: "include",
		...init,
		headers: { "Content-Type": "application/json", ...init?.headers },
	});
	// (an error page that isn't JSON, a gateway's during an update: its status is what's said, not a parse error)
	const body = (await response.json().catch(() => {
		if (response.ok) throw new Error(t("The game server's answer was cut short: try again"));
		return {};
	})) as T & { readonly error?: string; readonly code?: string };
	// the status goes with the error: a refusal (4xx) is final, unlike a server or network hiccup; and its code, when the
	// server says which refusal it is
	if (!response.ok)
		throw Object.assign(
			new Error(
				(body.error && /^address session required$/iu.test(body.error) ? SIGNED_OUT_TEXT : body.error) ??
					(response.status >= 500
						? t("The game server is busy or restarting (<status>): try again in a moment. A payment already sent is safe: nothing is paid twice.", { status: response.status })
						: t("Request failed (<status>)", { status: response.status })),
			),
			{ status: response.status, code: body.code },
		);
	return body;
};

/** the address the rest of the page was last told this page is signed in with (null: not told yet) */
let signedInAddress: string | undefined | null = null;
/**
 * the sign-in changed (signed in, out, or another address, here or in another tab): the rest of the page is told,
 * so nothing it kept for the old address (the designs it wore, the skins it collected) carries over to the next
 */
/** told when the player signs in or out (the store's own items follow the wallet, 0.92) */
export const storeBuyerChanged = new EventTarget();

const noteSignedIn = (address: string | undefined): void => {
	const next = address?.toLowerCase();
	if (next === signedInAddress) return;
	signedInAddress = next;
	announceSignedIn(next);
	storeBuyerChanged.dispatchEvent(new Event("change"));
};

const shorten = (address: string): string =>
	`${address.slice(0, 6)}…${address.slice(-4)}`;

/** words the server gave (a room's name, its entry's label) in the language picked; none when it gave none */
const trMaybe = (text: string | undefined): string | undefined => (text === undefined ? undefined : tr(text));

let stickyError: { readonly text: string; readonly until: number } | undefined;
/** errors stay visible for a few seconds instead of being replaced by the next lobby refresh */
const renderError = (text: string): void => {
	stickyError = { text, until: Date.now() + 8_000 };
	render(text);
};
/**
 * what an action ended with (not an error: "You left…", "Nothing was charged"): kept on screen for a few seconds as
 * an error is (said with render(), it was replaced at once by the redraw that ends every action)
 */
const renderNotice = renderError;

/**
 * (1.05) the menu's big button: what it does decides how it looks (menu.scss): joining (Play as guest, Register, Pay
 * to join...) in amber with the room it's for at its end, leaving (the queue, a lone paid seat, the auto-join)
 * outlined, a state (the seat paid, let in, out of the round) greyed
 */
type ActionKind = "join" | "leave" | "state";
const markAction = (action: HTMLElement, kind: ActionKind): void => {
	if (action.dataset.kind !== kind) action.dataset.kind = kind;
	// (a long label a size smaller: Russian's and German's register twice English's, and some languages' letters come
	// from a wider font than the game's own)
	const length = (action.textContent ?? "").trim().length;
	action.classList.toggle("long-label", length > 22);
	action.classList.toggle("longer-label", length > 32);
	renderPlayInfo();
};

/**
 * (1.05) a card's room name a size smaller when it doesn't fit its card at its size (a language's long word, a font
 * wider than the game's own), worked out from its size, so it never goes back and forth
 */
const fitRoomName = (name: HTMLElement): void => {
	name.classList.remove("tight");
	if (name.scrollWidth > name.clientWidth + 1) name.classList.add("tight");
};

/** the big button's right side: the room it joins and what that room is doing (its countdown, each second) */
const renderPlayInfo = (): void => {
	const action = document.getElementById("btn-wallet-action");
	const info = document.getElementById("menu-play-info");
	const name = document.getElementById("menu-play-room");
	const state = document.getElementById("menu-play-state");
	if (!action || !info || !name || !state) return;
	const room = shownRooms().find(({ definition }) => definition.id === selectedRoomId);
	// (signed out, Play as guest makes a guest, who plays the free room: its room said only when it's that one)
	const show =
		action.dataset.kind === "join" &&
		room !== undefined &&
		!action.classList.contains("secondary") &&
		(session !== undefined || room.definition.entryPolicy.kind === "free");
	action.classList.toggle("has-info", show);
	if (info.hidden === show) info.hidden = !show;
	if (!show) return;
	const roomName = tr(shortRoomName(room));
	const label = tr(roomStateFor(room).label);
	if (name.textContent !== roomName) name.textContent = roomName;
	if (state.textContent !== label) state.textContent = label;
};

/**
 * (1.05) what's on now, in the menu's top bar (from the rooms' status, as their cards): the rounds being played and the
 * players in them, and the players registered for the next ones; nothing while nothing is
 */
const renderLiveCounts = (): void => {
	const line = document.getElementById("menu-live");
	if (!line) return;
	const rooms = roundStatus?.rooms ?? [];
	const live = rooms.filter(({ activeRound }) => activeRound?.state === "in_progress");
	const count = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0);
	const playing = live.reduce((sum, { activeRound }) => sum + count(activeRound?.playingCount), 0);
	const registered = rooms.reduce((sum, { nextRound }) => sum + count(nextRound?.registeredCount), 0);
	const figure = (n: number): string => `<b>${n}</b>`;
	const parts: string[] = [];
	if (live.length) {
		parts.push(`<span><i class="menu-live-dot"></i>${playing === 1 ? t("<n> player in battle", { n: figure(playing) }) : t("<n> players in battle", { n: figure(playing) })}</span>`);
		parts.push(`<span>${live.length === 1 ? t("<n> round live", { n: figure(1) }) : t("<n> rounds live", { n: figure(live.length) })}</span>`);
	}
	if (registered) parts.push(`<span>${t("<n> registered", { n: figure(registered) })}</span>`);
	const html = parts.join("");
	if (line.dataset.shown !== html) {
		line.dataset.shown = html;
		line.innerHTML = html;
	}
	line.hidden = !parts.length;
};

const renderPanel = (message?: string): void => {
	// a press under way: what it said last stays on screen (with a hint once it has taken long)
	if (message && actionInFlightSince !== undefined) {
		if (message !== flowStatus) flowStatusSince = Date.now();
		flowStatus = message;
	} else if (!message && actionInFlightSince !== undefined && flowStatus)
		message =
			Date.now() - flowStatusSince > 90_000 && !flowStatus.includes(t("Taking long? Answer in your wallet if it's asking, or reload the page (nothing is paid twice)."))
				? sentences(flowStatus.replace(/[.…:。：।]*\s*$/u, "…"), t("Taking long? Answer in your wallet if it's asking, or reload the page (nothing is paid twice)."))
				: flowStatus;
	// waiting on the wallet app: where to look for it (it doesn't always pop up), until it answers
	if (!message && walletPending) message = walletWaitHint();
	if (!message && stickyError && Date.now() < stickyError.until)
		message = stickyError.text;
	const address = document.getElementById("wallet-address");
	const verification = document.getElementById("wallet-verification");
	const status = document.getElementById("wallet-status");
	const termsContainer = document.getElementById("wallet-terms-label");
	const action = document.getElementById(
		"btn-wallet-action",
	) as HTMLButtonElement | null;
	const change = document.getElementById(
		"btn-wallet-change",
	) as HTMLButtonElement | null;
	const verify = document.getElementById(
		"btn-wallet-verify",
	) as HTMLButtonElement | null;
	const testControls = document.getElementById("dev-test-controls");
	const testAction = document.getElementById(
		"btn-dev-test",
	) as HTMLButtonElement | null;
	const testSpectateAction = document.getElementById(
		"btn-dev-spectate",
	) as HTMLButtonElement | null;
	const publicSpectateAction = document.getElementById(
		"btn-spectate-current",
	) as HTMLAnchorElement | null;
	const profileAction = document.getElementById(
		"btn-profile",
	) as HTMLAnchorElement | null;
	const play = document.getElementById(
		"btn-play-solo",
	) as HTMLButtonElement | null;
	const roomLabel = document.getElementById("room-selector-label");
	const roomSelector = document.getElementById(
		"room-selector",
	) as HTMLSelectElement | null;
	const paymentPanel = document.getElementById("entry-payment-panel");
	if (
		!address ||
		!verification ||
		!status ||
		!termsContainer ||
		!action ||
		!change ||
		!verify ||
		!testControls ||
		!testAction ||
		!testSpectateAction ||
		!publicSpectateAction ||
		!profileAction ||
		!play ||
		!roomLabel ||
		!roomSelector ||
		!paymentPanel
	)
		return;

	const guest = session?.guest === true;
	address.textContent = myDisplayName() ?? t("Sign in");
	const subaddress = document.getElementById("wallet-subaddress");
	if (subaddress) {
		// a guest's made-up address means nothing to anyone: just its Guest name
		subaddress.hidden = !session || !myName || guest;
		subaddress.textContent = session ? shorten(session.address) : "";
	}
	// a chosen name comes with a wallet (a guest is Guest-1234)
	const editName = document.getElementById("btn-edit-name");
	if (editName) editName.hidden = !session || guest;
	// the nickname field (Customize box): there while signed in with a wallet, showing the saved name unless you're typing in it
	const nameEditor = document.getElementById("name-editor");
	const nameInput = document.getElementById(
		"name-input",
	) as HTMLInputElement | null;
	if (nameEditor) {
		nameEditor.hidden = !session || guest;
		// (open while it's being used, or for a wallet with no nickname yet)
		nameEditor.classList.toggle("open", nameEditorOpen || nameEdited || (Boolean(session) && !myName));
	}
	if (!session || guest) closeNameEditor();
	else if (nameInput && !nameEdited && document.activeElement !== nameInput)
		nameInput.value = myName ?? "";
	verification.hidden = !session;
	verification.textContent = guest ? t("GUEST") : t("VERIFIED");
	verification.classList.toggle("verified", session?.verified === true);
	verification.classList.toggle("guest", guest);
	termsContainer.hidden = Boolean(session);
	// signing out: a guest can't come back (it has no wallet), so it's asked first (see signOut)
	change.hidden = !session;
	// (while a press of the main button is on its way, the other buttons wait too: the text above them changes, they
	// move up, and the second click of a double click on Play as guest landed on Sign in with wallet)
	change.disabled = walletPending !== undefined || actionBusy();
	change.title = registration && registration.state !== "playing"
		? roomIsPaid(registration.roomId)
			? t("Your paid seat stays yours when you sign out: sign in again with this wallet to play it")
			: t("Signing out gives up your place in the queue")
		: guest
			? t("A guest can't come back once signed out: next time you're a new guest")
			: t("Sign out of this wallet on this browser");
	// signing in with a wallet: from signed out, or a guest moving to their wallet (it starts fresh)
	verify.hidden = Boolean(session && !guest);
	// while the wallet is asked: Cancel (a wallet that never answers used to leave every button disabled until a reload)
	const cancellable = walletPending !== undefined && walletStep !== "check";
	const verifyLabel = document.getElementById("btn-wallet-verify-label");
	if (verifyLabel) verifyLabel.textContent = cancellable ? t("Cancel") : t("Sign in with wallet");
	verify.disabled = (walletPending !== undefined && !cancellable) || actionBusy();
	verify.title = guest && registration && registration.state !== "playing"
		? t("Your guest's place in the queue moves to your wallet, if it can")
		: t("Your wallet asks you to sign a message. It's free: no payment, nothing is sent.");
	// the button this device used last time goes first (a wallet player isn't sent through the guest button)
	const walletFirst = !session && lastSignIn() === "wallet";
	verify.classList.toggle("primary", walletFirst);
	action.classList.toggle("secondary", walletFirst);
	// (1.05) where it goes: signed out, with Play as guest under the rooms, the one used last first; a guest's in the top
	// bar, by their name. Moved only when it isn't there (moving a button every second would take the keyboard focus
	// off it)
	const playRow = action.parentElement;
	const signInSlot = document.getElementById("menu-sign-in-slot");
	if (session) {
		if (signInSlot && verify.parentElement !== signInSlot) signInSlot.append(verify);
	} else if (playRow) {
		if (walletFirst ? playRow.previousElementSibling !== verify : playRow.nextElementSibling !== verify) {
			if (walletFirst) playRow.before(verify);
			else playRow.after(verify);
		}
	}
	// the menu's look follows who's there: nobody yet, a guest, a wallet (menu.scss)
	const menu = document.getElementById("splash-ui");
	const who = !session ? "none" : guest ? "guest" : "wallet";
	if (menu && menu.dataset.session !== who) menu.dataset.session = who;
	testControls.hidden = !(session && developmentTestMode);
	const inRunningRound = registration?.state === "playing";
	const quickStart = document.getElementById(
		"btn-dev-quickstart",
	) as HTMLButtonElement | null;
	if (quickStart) {
		// no bots in paid rooms: their rounds start by the real rules, with real players only
		const registeredRoom = registration
			? roundStatus?.rooms.find(({ definition }) => definition.id === registration?.roomId)?.definition
			: undefined;
		const paidRegistration = registeredRoom !== undefined && registeredRoom.entryPolicy.kind !== "free";
		quickStart.disabled =
			!registration || inRunningRound || Boolean(roundStatus?.activeRound) || paidRegistration;
		quickStart.title = !registration
			? t("Register for the next round first")
			: paidRegistration
				? t("Quick start (with bots) is for the Free room only: paid rounds are played by real players only")
				: roundStatus?.activeRound
					? t("A round is still running")
					: t("Start the round now; bots fill the empty seats");
	}
	testAction.disabled =
		inRunningRound ||
		Boolean(developmentAdmission) ||
		Boolean(activeDevelopmentTest);
	testAction.textContent = activeDevelopmentTest
		? t("Test battle in progress")
		: t("Enter test battle");
	testAction.title = inRunningRound
		? t("You are playing in the running public round")
		: activeDevelopmentTest
			? t("This session already has an active test battle")
			: registration
				? t("Start a private bot battle; the ready check is confirmed for you and you are moved to the public round when it starts")
				: t("Start a private bot battle");
	// one Spectate button in the menu grid covers every case (the sandbox copy is no longer needed)
	testSpectateAction.hidden = true;
	// Spectate opens the list of running games under the server name
	const watchable = runningGames.length;
	publicSpectateAction.hidden = false;
	publicSpectateAction.dataset.mode = watchable ? "list" : "none";
	publicSpectateAction.classList.toggle("btn-primary", watchable > 0);
	publicSpectateAction.classList.toggle("btn-secondary", watchable === 0);
	publicSpectateAction.classList.toggle(
		"is-disabled",
		watchable === 0 || Boolean(developmentAdmission),
	);
	publicSpectateAction.setAttribute("aria-disabled", String(watchable === 0));
	publicSpectateAction.title = watchable
		? t("Choose a game to watch")
		: t("Nothing to watch right now");
	const spectateLabel = publicSpectateAction.querySelector("span");
	if (spectateLabel) {
		spectateLabel.textContent = t("Spectate");
		if (watchable) {
			const badge = document.createElement("span");
			badge.className = "spectate-count";
			badge.textContent = String(watchable);
			spectateLabel.appendChild(badge);
		}
	}

	profileAction.hidden = !session;
	// the winnings panel (a win and what became of it; a guest's, and where it goes)
	renderPayouts();
	const rooms = shownRooms();
	roomLabel.hidden = !session || rooms.length < 2;
	if (rooms.length) {
		const optionKey = rooms
			.map(({ definition, availability, entryLabel }) =>
				[definition.id, availability, entryLabel].join(":"),
			)
			.join();
		if (roomSelector.dataset.rooms !== optionKey) {
			roomSelector.replaceChildren(
				...rooms.map((room) =>
					Object.assign(document.createElement("option"), {
						value: room.definition.id,
						// (what the room is doing now: kept up to date every second, tickRoomStates)
						textContent: roomOptionText(room),
						disabled: room.availability === "coming_soon",
					}),
				),
			);
			roomSelector.dataset.rooms = optionKey;
		}
		if (
			!rooms.some(
				({ definition, availability }) =>
					definition.id === selectedRoomId && availability === "available",
			)
		)
			selectedRoomId =
				rooms.find(({ availability }) => availability === "available")
					?.definition.id ?? "free";
	}
	if (registration) selectedRoomId = registration.roomId;
	roomSelector.value = selectedRoomId;
	// a payment under way keeps you in its room (from the press of the button: the room is the one it pays for); a
	// finished one (played, refunded, run out) doesn't
	roomSelector.disabled = Boolean(registration || paymentUnderWay(entryIntent) || paymentFor !== undefined || actionBusy());
	// what each room is doing now, in the picker and in the list under it
	tickRoomStates();
	const selectedRoomState = rooms.find(
		({ definition }) => definition.id === selectedRoomId,
	);
	const selectedRoom = selectedRoomState?.definition;
	// a seat's room says whether it's paid, even before the room list has loaded (a paid seat always gets the paid seat's
	// button and its question: it was shown "Leave queue", and leaving cancelled a lone paid round without asking);
	// otherwise the room picked
	const paidRoom = registration ? roomIsPaid(registration.roomId) : selectedRoom !== undefined && selectedRoom.entryPolicy.kind !== "free";
	// in a paid room's queue: the seat is paid for (whatever became of the request the payment was made with)
	const paidSeat = Boolean(registration && paidRoom && registration.state !== "playing");
	const shownIntent = entryIntent?.roomId === selectedRoomId ? entryIntent : undefined;
	// an earlier payment from this browser the game lost (a restart): said until it's back in the wallet that paid it
	const lost = !shownIntent && !paidSeat && paidRoom ? lostPaymentFor(selectedRoomId) : undefined;
	paymentPanel.hidden = !shownIntent && !paidSeat && !lost;
	if (shownIntent || paidSeat || lost) {
		// one plain line for players: the technical details (vault, IDs) are for admins, in the Control Room
		// (the room list not loaded yet: the request's own amount, or none)
		const known =
			selectedRoomState?.entryLabel ??
			(shownIntent
				? isStakeRoom(shownIntent.roomId)
					? `${coinText(shownIntent.amountAtomic, shownIntent.asset.tokenDecimals)} USDG`
					: t("<amount> tokens", { amount: coinText(shownIntent.amountAtomic, shownIntent.asset.tokenDecimals) })
				: undefined);
		const price = trMaybe(known) ?? t("the entry");
		// a payment in a round its payer couldn't play in (the server says so; or seen on the blockchain, its request
		// over, and no seat)
		const strayPayment = strayEntry(shownIntent);
		const [text, tone] = paidSeat
			? ([`✓ ${known ? t("Paid <price>. Your seat in the next round is kept.", { price }) : t("Paid. Your seat in the next round is kept.")}`, "ok"] as const)
			: lost
				? ([lostPaymentText(lost), "wait"] as const)
				: strayPayment
					? ([t("Your <price> payment is in a round you couldn't play in: an admin decides what happens to it.", { price }), "bad"] as const)
					: paymentStatus(shownIntent?.state ?? "pending", price);
		const status = document.getElementById("entry-payment-status");
		if (status) status.textContent = text;
		paymentPanel.dataset.tone = tone;
	}
	play.disabled = !inRoundToPlay() && !developmentAdmission;
	play.classList.toggle("locked", play.disabled);

	// the session check failing for a while (the server out of reach, an update going in): said, and the last known
	// state kept meanwhile
	if (!message && sessionCheckFailingSince !== undefined && Date.now() - sessionCheckFailingSince > 5_000)
		message = t("Can't reach the game server right now: trying again…");
	if (message) status.textContent = message;
	if (!session) {
		action.textContent = t("Play as guest");
		action.title = t("No wallet needed: the free room and the Training Range");
		action.disabled = walletPending !== undefined || actionBusy();
		markAction(action, "join");
		if (!message)
			status.textContent = walletFirst
				? t("Welcome back. Sign in with your wallet for every room, or play as a guest (the free room and the Training Range).")
				: t("Play as a guest: the free room and the Training Range, no wallet needed. Or sign in with your wallet (MetaMask, Rabby…) for every room and to keep your stats.");
		updateTitleAlert(false);
		return;
	}

	action.disabled = actionBusy() || walletPending !== undefined;
	action.title = "";
	// the room's own next round (each room has its own: a paid room never goes by another room's round, the Free room's
	// listed first included)
	const nextRound = selectedRoomState?.nextRound ?? (paidRoom ? undefined : roundStatus?.nextRound);
	updateTitleAlert(
		Boolean(
			registration &&
				registration.state !== "playing" &&
				nextRound?.state === "countdown" &&
				document.hidden,
		),
	);
	updateAdmittedAlert(Boolean(inRoundToPlay() && document.hidden));
	if (registration?.state === "playing" && registration.knockedOut !== undefined) {
		// out of the running round: it's still theirs until it ends (one round at a time), to watch
		action.textContent = t("Out of this round");
		action.disabled = true;
		markAction(action, "state");
		if (!message)
			status.textContent =
				registration.knockedOut > 0
					? t("You're out of this round (#<place>). Watch the rest of it with Spectate; you can register again once it ends.", { place: registration.knockedOut })
					: t("You're out of this round. Watch the rest of it with Spectate; you can register again once it ends.");
	} else if (
		registration?.state === "playing" &&
		autoJoin &&
		autoJoin.roundId === registration.roundId
	) {
		const left = Math.max(0, Math.ceil((autoJoin.at - Date.now()) / 1000));
		action.textContent = t("Cancel auto-join (<s>)", { s: left });
		markAction(action, "leave");
		if (!message)
			status.textContent = t("You're in! Entering the battle in <s>\u2026", { s: left });
	} else if (registration?.state === "playing") {
		action.textContent = t("Admitted to battle");
		action.disabled = true;
		markAction(action, "state");
		if (!message)
			status.textContent = t("Your place is confirmed. Press Enter battle below.");
	} else if (registration) {
		// registered means ready: the round starts when the wait runs out (or the room fills). A paid seat is kept:
		// its entry is in the vault under this round (it's played, or cancelled with everyone refunded). The only
		// player in it can still leave: the round is cancelled, and their entry refunded. (Only once the room list says
		// so: until it has loaded, the paid seat's button waits)
		const seatRound = nextRound?.id === registration.roundId ? nextRound : undefined;
		const alone = seatRound !== undefined && seatRound.registeredCount <= 1 && seatRound.state !== "countdown";
		action.textContent = paidRoom ? (alone ? t("Leave & get refunded") : `${t("Seat paid")} \u2713`) : t("Leave queue");
		action.disabled = (paidRoom && !alone) || actionBusy();
		markAction(action, paidRoom && !alone ? "state" : "leave");
		if (paidRoom)
			action.title = alone
				? t("You're the only one in this round: leaving cancels it, and your entry goes back to the wallet that paid it")
				: seatRound
					? t("Others have paid for this round too, so your seat is kept until it's played (or cancelled, with everyone refunded)")
					: t("Your seat is kept until the round is played (or cancelled, with everyone refunded). Checking the round\u2026");
		// counting down, the players toward a full round (the room's: 30 in the Token room); before that, toward its
		// start ("1 of 10"), as the room list counts them ("Open · 1/10": the line under it said "1/25")
		// (the room picked is the seat's: see selectedRoomId above)
		const limits = limitsOf(selectedRoom);
		const count = nextRound ? t("<count>/<max> players", { count: nextRound.registeredCount, max: limits.maxPlayers }) : "";
		// the real rules in every mode; in development Quick start can begin the round now, with bots (Free room only)
		const minimum = limits.minPlayers;
		const toStart = nextRound ? t("<count> of <min>", { count: nextRound.registeredCount, min: minimum }) : undefined;
		const quickStartHint = developmentTestMode && !paidRoom
			? t("Or press Quick start to play now with bots.")
			: "";
		if (!message) {
			status.textContent =
				nextRound?.state === "countdown" && nextRound.startsAt
					? selectedRoomState?.activeRound ?? (paidRoom ? undefined : roundStatus?.activeRound)
						? t("Next round: <count>. It starts as soon as the current round ends.", { count })
						: serverNow() - nextRound.startsAt > 3_000
							? paidRoom
								? t("Next round: <count>. It starts in a moment (once the payments still going through are done, and a game is free).", { count })
								: t("Next round: <count>. It starts as soon as a game is free on the server.", { count })
							: t("Starting in <s> s \u00B7 <count>", { s: secondsUntil(nextRound.startsAt), count })
					: paidRoom
						? sentences(
								toStart
									? t("Paid and registered (<progress>). The round starts once <min> players have joined.", { progress: toStart, min: minimum })
									: t("Paid and registered. The round starts once <min> players have joined.", { min: minimum }),
								paidSeatRule(),
								alone && t("While you're the only one in it, you can leave and get your entry back."),
								feePoolPrize(selectedRoomId),
							)
						: sentences(
								toStart
									? t("Registered (<progress>). The round starts once <min> players have joined.", { progress: toStart, min: minimum })
									: t("Registered. The round starts once <min> players have joined.", { min: minimum }),
								quickStartHint,
							);
		}
	} else {
		action.textContent = paidRoom
			? paymentGoingIn(entryIntent)
				? entryIntent.state === "confirmed"
					? t("Confirm paid entry")
					: t("Check my payment")
				: entryIntent && !finishedIntent(entryIntent) && !strayEntry(entryIntent)
						? entryIntent.execution
							? sentFromHere(entryIntent.id)
								? t("Check my payment")
								: t("Pay & join")
							: t("Refresh payment status")
						: entryIntent?.state === "consumed" || entryIntent?.state === "refund_pending"
							? t("Pay for the next round")
							: lost
								? t("Pay again to join")
								: t("Pay to join")
			: t("Register for next round");
		markAction(action, "join");
		if (!message) {
			status.textContent = activeDevelopmentTest
				? t("A <n>-bot test battle is still active. You can spectate it below.", { n: activeDevelopmentTest.botCount })
				: paidRoom && entryIntent?.state === "consumed"
					? // used for the round still taking players: they're in it (the next check shows their seat)
						selectedRoomState?.nextRound?.id === entryIntent.roundId
						? t("Paid: you're in the round. Your seat shows in a moment.")
						: sentences(t("Your last paid round is over. Pay again to play another paid round."), feePoolPrize(selectedRoomId))
					: paidRoom && strayEntry(entryIntent)
						? t("Your last payment is in a round you couldn't play in: an admin decides what happens to it. You can pay to join the next round.")
						: paidRoom && entryIntent?.state === "refund_pending"
						? sentences(
								entryIntent.payer
									? t("That round was cancelled, so your entry goes back to the wallet that paid it (<wallet>). Pay again to join the next round.", { wallet: shorten(entryIntent.payer) })
									: guest
										? t("That round was cancelled, so your entry goes back to the wallet that paid it. Pay again to join the next round.")
										: t("That round was cancelled, so your entry goes back to your wallet. Pay again to join the next round."),
								feePoolPrize(selectedRoomId),
							)
						: paidRoom && !session.verified && !(guest && isStakeRoom(selectedRoomId))
							? t("Sign in with your wallet to play paid rooms.")
							: paidRoom && paymentGoingIn(entryIntent)
								? // paid already (it used to say "Ready to pay" here): only checked from now on
									sentences(
										paymentStatus(entryIntent.state, trMaybe(selectedRoomState?.entryLabel) ?? t("the entry fee"))[0],
										entryIntent.state === "confirming"
											? t("You're let in once it's confirmed: press Check my payment if it takes more than a minute. Nothing more is paid.")
											: t("You're let in once it's confirmed. Nothing more is paid."),
									)
							: paidRoom && entryIntent && !finishedIntent(entryIntent)
								? entryIntent.execution
									? sentFromHere(entryIntent.id)
										? t("Your payment was sent from this browser: press Check my payment if your seat doesn't show in a minute (nothing is sent twice).")
										: t("Ready to pay: your wallet will ask you to confirm twice (allow the exact amount, then pay). Once paid, your seat is kept until the round is played.")
									: sentences(
											paymentStatus(entryIntent.state, trMaybe(selectedRoomState?.entryLabel) ?? t("the entry fee"))[0],
											t("You're let in once the payment is confirmed on the blockchain."),
										)
								: paidRoom && lost
									? t("Your earlier payment wasn't seen by the game, so it isn't a seat: it's refunded to the wallet that paid it (see above). Paying again is a new payment, for this round.")
									: session.verified
										? paidRoom
											? sentences(
													t("Verified. Pay the entry fee to join this room's next round (real players only, no bots)."),
													paidSeatRule(),
													feePoolPrize(selectedRoomId),
													bettingPrize(selectedRoomId),
												)
											: t("Verified. This room is free to enter.")
										: paidRoom
											? sentences(
													t("You're playing as a guest. Pay the entry from a wallet on Robinhood Chain (real players only, no bots). If you win, you choose where the pot goes."),
													paidSeatRule(),
													bettingPrize(selectedRoomId),
												)
											: entryMethods?.guestsPay
												? t("You're playing as a guest: the free room, the Training Range, and the USDG rooms (pay the entry from a wallet on Robinhood Chain). A wallet sign-in picks a name and keeps its stats for good (a guest's go with it).")
												: t("You're playing as a guest: the free room and the Training Range. A wallet plays every room, picks a name and keeps its stats for good (a guest's go with it).");
		}
	}
};

/**
 * what the panel says, read out by a screen reader (the hidden #wallet-announce): only when more than its numbers
 * change. The panel itself was the live region, so every second of a countdown ("Entering the battle in 5…", a
 * room's "Starting 0:45") was read out, and the same words again each time they were redrawn. Not the menu's first
 * words: a screen reader reads the page itself
 */
let announcedGist: string | undefined;
const announcePanel = (): void => {
	const region = document.getElementById("wallet-announce");
	if (!region) return;
	const text = ["wallet-status", "entry-payment-status", "payout-text"]
		.map((id) => document.getElementById(id))
		.filter((element): element is HTMLElement => element !== null && element.closest("[hidden]") === null)
		.map((element) => element.textContent?.trim() ?? "")
		.filter(Boolean)
		.join(" ");
	const gist = text.replace(/\d+/gu, "#");
	if (gist === announcedGist) return;
	const first = announcedGist === undefined;
	announcedGist = gist;
	if (!first && region.textContent !== text) region.textContent = text;
};

const render = (message?: string): void => {
	renderPanel(message);
	announcePanel();
	rulesFigures();
	renderLiveCounts();
};

/**
 * a USDG room's prize, as its vault pays it: the pot, less the vault's betting fee ("The winner gets 95% of the pot: 5%
 * keeps the game running."). Nothing while the fee isn't known
 */
const bettingPrize = (roomId: RoomId): string => {
	const bps = entryMethods?.usdg?.winnerFeeBps;
	if (!isStakeRoom(roomId) || bps === undefined || !Number.isInteger(bps) || bps < 0 || bps > 1_000) return "";
	return bps > 0
		? t("The winner gets <share> of the pot (<fee> keeps the game running).", { share: feePercentText(10_000 - bps), fee: feePercentText(bps) })
		: t("The winner gets the whole pot.");
};

/**
 * the Token room's prize, said once the fee pool pays: "The winner gets up to 70% of the fee pool: about 12.50 USDG
 * right now." The most a winner gets is never said (the owner's decision): "up to 70%", as the Rules say, and once a
 * winner's 70% would be more than that most (the server says so: `amountOnly`), the amount they get, without the
 * percent. While rounds before it are still to be paid (one being played, say), the prize is what they leave, and the
 * line says so (0.83)
 */
const feePoolPrize = (roomId: RoomId): string => {
	const pool = roundStatus?.feePool;
	if (!isTokenRoomId(roomId) || !pool) return "";
	// (an answer that isn't whole numbers says nothing: the menu never breaks over it)
	if (!/^\d{1,40}$/u.test(String(pool.winnerAtomic)) || !Number.isInteger(pool.tokenDecimals) || pool.tokenDecimals < 0 || pool.tokenDecimals > 36) return "";
	const unit = 10n ** BigInt(pool.tokenDecimals);
	// to the cent (rounded down: never more than will be paid)
	const coins = (atomic: bigint) => {
		const cents = (atomic * 100n) / unit;
		return `${(cents / 100n).toLocaleString("en-US")}.${(cents % 100n).toString().padStart(2, "0")}`;
	};
	const share = BigInt(pool.winnerAtomic);
	// (at the most a winner gets, the share is no longer the percent of the pool: the amount alone, which is true)
	if (share > 0n && pool.amountOnly === true) return t("The winner gets <amount> from the fee pool right now.", { amount: `${coins(share)} ${pool.symbol}` });
	const before = Number.isInteger(pool.roundsBefore) && Number(pool.roundsBefore) > 0 ? Math.min(Number(pool.roundsBefore), 99) : 0;
	const percent = pool.winnerPercent;
	// (the share after the rounds paid first, if there are any: said in the same sentence)
	if (share > 0n) {
		const amount = `${coins(share)} ${pool.symbol}`;
		return before === 0
			? t("The winner gets up to <percent>% of the fee pool: about <amount> right now (it grows with the trading fees).", { percent, amount })
			: before === 1
				? t("The winner gets up to <percent>% of the fee pool left after the round before it: about <amount> right now (it grows with the trading fees).", { percent, amount })
				: t("The winner gets up to <percent>% of the fee pool left after the <n> rounds before it: about <amount> right now (it grows with the trading fees).", { percent, n: before, amount });
	}
	return before === 0
		? t("The winner gets up to <percent>% of the fee pool (it fills with the trading fees).", { percent })
		: before === 1
			? t("The winner gets up to <percent>% of the fee pool left after the round before it (it fills with the trading fees).", { percent })
			: t("The winner gets up to <percent>% of the fee pool left after the <n> rounds before it (it fills with the trading fees).", { percent, n: before });
};

type RoomStatusRoom = RoundStatus["rooms"][number];

/**
 * the rooms this player can pick: a guest plays the free room, and the USDG rooms when guests can pay them here (from
 * a wallet app); the Token room is for players signed in with their wallet
 */
const shownRooms = (): readonly RoomStatusRoom[] => {
	const guest = session?.guest === true;
	return (roundStatus?.rooms ?? []).filter(
		({ definition }) =>
			!guest ||
			definition.entryPolicy.kind === "free" ||
			(isStakeRoom(definition.id) && entryMethods?.guestsPay === true) ||
			// the guest's own seat, or payment, is in it (whatever the server said about guests paying, or couldn't say)
			definition.id === registration?.roomId ||
			(definition.id === entryIntent?.roomId && paymentUnderWay(entryIntent)),
	);
};

/**
 * a room's round limits as the server gives them, when they make sense (how many a round needs, and takes: 25, or 30
 * in the Token room), else the usual ones
 */
const limitsOf = (definition: RoomStatusRoom["definition"] | undefined): { readonly minPlayers: number; readonly maxPlayers: number } => {
	const limits = definition?.limits;
	const valid = limits && Number.isInteger(limits.minPlayers) && Number.isInteger(limits.maxPlayers) && limits.minPlayers >= 1 && limits.maxPlayers >= limits.minPlayers;
	return valid ? limits : ROUND_LIMITS;
};

/** what a room is doing now: a round in progress, the next starting (its countdown), full, open, paused */
const roomStateFor = (room: RoomStatusRoom): RoomStateView => {
	// in this room's round being played (or out of it already): they register again once it ends
	const inRound = registration?.state === "playing" && registration.roomId === room.definition.id && registration.roundId === room.activeRound?.id;
	// (in the menu's language: roomState.ts's phrases are the phrasebook's, 1.04.4)
	return roomStateOf({ ...room, inRound }, serverNow(), limitsOf(room.definition), t);
};

/** a room's name in the list, short: "10 USDG Entry" is the 10 USDG room ("10 USDG") */
const shortRoomName = (room: RoomStatusRoom): string => room.definition.displayName.replace(/\s+entry$/iu, "") || room.definition.displayName;

/** a room's price, unless its name says it already ("Free", "10 USDG"): "100 BOOM" */
const roomPriceText = (room: RoomStatusRoom, name = room.definition.displayName): string =>
	name.toLowerCase().includes(room.entryLabel.toLowerCase()) ? "" : room.entryLabel;

/**
 * Every room the player can pick, with what it's doing now (owner request, October 2026): "In progress", "Starting
 * 0:45", "Full", "Open · 4/10", and for the room picked a line saying more; the rooms not open yet on one line under
 * them. A press picks the room (as the picker does), unless a seat or a payment holds the player in theirs. Drawn once
 * per set of rooms, then only its words change (the countdowns, every second). Screen readers aren't told every
 * second: the list and its line aren't live regions (nor is the panel: announcePanel), a room's words are read when it's
 * reached
 */
const renderRoomStates = (rooms: readonly RoomStatusRoom[], locked: boolean): void => {
	const list = document.getElementById("room-states");
	const detail = document.getElementById("room-state-detail");
	const soon = document.getElementById("room-states-soon");
	if (!list || !detail || !soon) return;
	const open = rooms.filter((room) => room.availability !== "coming_soon");
	const later = rooms.filter((room) => room.availability === "coming_soon");
	// (1.05) a guest sees the open rooms a wallet plays too, as locked cards: signing in with a wallet opens them
	const walletOnly =
		session?.guest === true
			? (roundStatus?.rooms ?? []).filter((room) => room.availability !== "coming_soon" && !rooms.includes(room))
			: [];
	list.hidden = rooms.length + walletOnly.length < 2 || !open.length;
	detail.hidden = list.hidden;
	soon.hidden = list.hidden || !later.length;
	// the list is the room picker where the screen is tall enough for it; on a short one (a phone on its side) the
	// picker takes its place, its rooms saying the same (splash.scss)
	document.getElementById("room-selector-label")?.classList.toggle("has-list", !list.hidden);
	if (list.hidden) return;
	// (by their short names, as in the list: "100 USDG", not "100 USDG Entry")
	const soonText = t("Coming soon: <rooms>.", { rooms: later.map((room) => tr(shortRoomName(room))).join(", ") });
	if (!soon.hidden && soon.textContent !== soonText) soon.textContent = soonText;
	const key = [...open.map(({ definition }) => definition.id), ...walletOnly.map(({ definition }) => `wallet:${definition.id}`)].join();
	/** a room's card: its name and its price under it, what it's doing over them (menu.scss) */
	const card = (definition: RoomStatusRoom["definition"], className: string): { readonly item: HTMLLIElement; readonly button: HTMLButtonElement } => {
		const item = document.createElement("li");
		const button = Object.assign(document.createElement("button"), { type: "button", className });
		button.dataset.room = definition.id;
		const what = Object.assign(document.createElement("span"), { className: "room-state-what" });
		const name = Object.assign(document.createElement("span"), { className: "room-state-name" });
		const price = Object.assign(document.createElement("span"), { className: "room-state-price" });
		what.append(name, price);
		const chip = Object.assign(document.createElement("span"), { className: "room-state-chip" });
		button.append(what, chip);
		item.append(button);
		return { item, button };
	};
	if (list.dataset.rooms !== key) {
		list.replaceChildren(
			...open.map(({ definition }) => {
				const { item, button } = card(definition, "room-state");
				button.addEventListener("click", () => {
					const roomId = button.dataset.room as RoomId | undefined;
					const picker = document.getElementById("room-selector") as HTMLSelectElement | null;
					if (!roomId || button.disabled || !picker || picker.disabled || roomId === selectedRoomId) return;
					picker.value = roomId;
					picker.dispatchEvent(new Event("change"));
				});
				return item;
			}),
			// (a guest's: pressed, it says how to play it, and the way to the wallet sign-in is next)
			...walletOnly.map((room) => {
				const { item, button } = card(room.definition, "room-state wallet-only");
				button.dataset.kind = "wallet";
				button.setAttribute("aria-disabled", "true");
				button.addEventListener("click", () => {
					renderNotice(t("Sign in with your wallet to play the <room> room.", { room: tr(shortRoomName(room)) }));
					document.getElementById("btn-wallet-verify")?.focus();
				});
				return item;
			}),
		);
		list.dataset.rooms = key;
	}
	for (const room of walletOnly) {
		const button = list.querySelector<HTMLButtonElement>(`button[data-room="${room.definition.id}"]`);
		if (!button) continue;
		const shortName = shortRoomName(room);
		const shownName = tr(shortName);
		const priceText = tr(roomPriceText(room, shortName));
		const label = t("Wallet only");
		const name = button.querySelector<HTMLElement>(".room-state-name");
		const price = button.querySelector<HTMLElement>(".room-state-price");
		const chip = button.querySelector<HTMLElement>(".room-state-chip");
		if (name && name.textContent !== shownName) name.textContent = shownName;
		if (name) fitRoomName(name);
		if (price && price.textContent !== priceText) price.textContent = priceText;
		if (chip && chip.textContent !== label) chip.textContent = label;
		button.title = t("<room>: sign in with your wallet to play it.", { room: tr(room.definition.displayName) });
	}
	for (const room of open) {
		const button = list.querySelector<HTMLButtonElement>(`button[data-room="${room.definition.id}"]`);
		if (!button) continue;
		const state = roomStateFor(room);
		const name = button.querySelector<HTMLElement>(".room-state-name");
		const price = button.querySelector<HTMLElement>(".room-state-price");
		const chip = button.querySelector<HTMLElement>(".room-state-chip");
		const shortName = shortRoomName(room);
		// (the server's words, compared as it gave them: shown in the language picked)
		const priceText = tr(roomPriceText(room, shortName));
		const shownName = tr(shortName);
		const label = tr(state.label);
		const said = tr(state.detail);
		if (name && name.textContent !== shownName) name.textContent = shownName;
		if (name) fitRoomName(name);
		if (price && price.textContent !== priceText) price.textContent = priceText;
		if (chip && chip.textContent !== label) chip.textContent = label;
		button.dataset.kind = state.kind;
		button.title = t("<room>: <detail>", { room: tr(room.definition.displayName), detail: said });
		const picked = room.definition.id === selectedRoomId;
		button.setAttribute("aria-pressed", String(picked));
		button.disabled = locked && !picked;
		if (picked && detail.textContent !== said) detail.textContent = said;
	}
	// the room picked isn't open (one not open yet): no line about another
	if (!open.some((room) => room.definition.id === selectedRoomId) && detail.textContent) detail.textContent = "";
};

/** the countdowns in the room list, every second (nothing else is read again) */
const tickRoomStates = (): void => {
	const picker = document.getElementById("room-selector") as HTMLSelectElement | null;
	renderRoomStates(shownRooms(), picker?.disabled === true);
	renderPlayInfo();
	// (the picker's words aren't changed while it's in use: a screen reader would read them again every second, and an
	// open list could lose its place)
	if (!picker || document.activeElement === picker) return;
	for (const option of Array.from(picker.options)) {
		const room = shownRooms().find(({ definition }) => definition.id === option.value);
		if (room) {
			const text = roomOptionText(room);
			if (option.textContent !== text) option.textContent = text;
		}
	}
};

/** a room in the picker: "Token · 100 BOOM · Starting 0:45" ("10 USDG Entry · Open": its name says its price) */
const roomOptionText = (room: RoomStatusRoom): string => {
	const price = roomPriceText(room);
	return `${tr(room.definition.displayName)}${price ? ` · ${tr(price)}` : ""} · ${tr(roomStateFor(room).label)}`;
};

/**
 * the coin an entry label names ("2,500 BOOM": BOOM), if it names one: a label without an amount ("Token entry",
 * "Free") names none (its last word was taken for the coin: "… entry", 1.04.5)
 */
const labelCoin = (label: string | undefined): string | undefined => /\d\s+([A-Za-z][A-Za-z0-9.]*)\s*$/u.exec(label ?? "")?.[1];

/** a room's coin, as its label names it ("10 USDG": USDG), else by the room */
const roomCoin = (roomId: RoomId): string =>
	labelCoin(roundStatus?.rooms.find(({ definition }) => definition.id === roomId)?.entryLabel) ?? (isStakeRoom(roomId) ? "USDG" : t("tokens"));

/**
 * what's said of a payment from this browser the game lost (a restart): it isn't a seat, and it's refunded to the wallet
 * that paid it: by the settlement bot when it's automatic (usually within about two hours), or by an admin
 */
const lostPaymentText = (lost: LostPayment): string => {
	const amount = `${exactCoinText(lost.amountAtomic, lost.tokenDecimals)} ${roomCoin(lost.roomId)}`;
	return lost.payer === session?.address && session?.guest !== true
		? t("Your earlier <amount> payment from this browser wasn't seen by the game (it restarted), so it isn't a seat. It's refunded to your wallet by the game's settlement bot (usually within about 2 hours) or by an admin.", { amount })
		: t("Your earlier <amount> payment from this browser wasn't seen by the game (it restarted), so it isn't a seat. It's refunded to the wallet that paid it (<wallet>) by the game's settlement bot (usually within about 2 hours) or by an admin.", { amount, wallet: shorten(lost.payer) });
};

/** what belongs to one player (a payment on screen, the room picked, their name, a message): dropped when the sign-in changes */
const resetPlayerState = (): void => {
	registration = null;
	// (a payment going through keeps its state until it's done: its flow still needs it)
	if (paymentFor === undefined) entryIntent = undefined;
	paidEntryAdopted = false;
	heldEntryId = undefined;
	latestPaidEntry = undefined;
	selectedRoomId = "free";
	myName = null;
	nameLoadedFor = undefined;
	stickyError = undefined;
	autoJoin = undefined;
	guestWins = [];
	walletWins = [];
	payerProof = undefined;
	activePayer = undefined;
	payoutFormOpen = false;
	// an address typed by the last player (where their winnings were to go) isn't the next one's
	clearAddressInputs();
};

/** the payout address box, emptied (another player on this page, or the winnings have gone there) */
const clearAddressInputs = (): void => {
	const input = document.getElementById("payout-address") as HTMLInputElement | null;
	if (input) input.value = "";
};

/** a payment's transaction, remembered on this browser while it's confirmed (a reload picks it up again): forgotten */
/** this browser sent this request's payment (its transaction is remembered: a reload in the middle checks it, never pays again) */
const sentFromHere = (intentId: string): boolean => {
	try {
		return localStorage.getItem(`trenchio.entryTransaction.${intentId}`) !== null;
	} catch {
		return false;
	}
};

const forgetEntryTransaction = (intentId: string): void => {
	try {
		localStorage.removeItem(`trenchio.entryTransaction.${intentId}`);
	} catch {
		/* storage unavailable */
	}
};

/** the 18+ box unticked: signed out, the next person on this computer ticks it themselves */
const untickTerms = (): void => {
	const terms = document.getElementById("wallet-terms") as HTMLInputElement | null;
	if (terms) terms.checked = false;
};

const refresh = async (): Promise<void> => {
	const seq = ++refreshSeq;
	const epoch = authEpoch;
	const askedAt = Date.now();
	const [authResult, statusResult] = await Promise.allSettled([
		request<SessionResponse>("/auth/session", { signal: checkSignal() }).then((answer) => {
			noteServerClock(answer.serverNow, askedAt, Date.now());
			return answer;
		}),
		request<RoundStatus>("/public/status", { signal: checkSignal() }),
	]);
	// a newer check came back first: this one's answer is older than what's shown
	if (seq < appliedSeq) return;
	appliedSeq = seq;

	if (statusResult.status === "fulfilled") roundStatus = statusResult.value;

	// signed in, out, or a place changed here since this check went out: its answer is from before (the next one isn't)
	if (epoch !== authEpoch) {
		render();
		return;
	}
	if (authResult.status === "fulfilled") {
		sessionCheckFailingSince = undefined;
		let auth = authResult.value;
		// signed out, says the answer, while signed in here: asked again at once before the menu lets go of the player (a
		// stray answer would end everything of theirs on the page)
		if (!auth.session && session) {
			try {
				auth = await request<SessionResponse>("/auth/session");
			} catch {
				/* the first answer stands */
			}
			if (seq < appliedSeq || epoch !== authEpoch) {
				render();
				return;
			}
		}
		// another sign-in (here or in another tab), a sign-out, a sign-in that ran out: nothing of the last player's stays
		if ((auth.session?.address ?? undefined) !== (session?.address ?? undefined)) {
			// but signing in from signed out keeps the room picked meanwhile: it's this player's (they were put back in
			// the free room by every sign-in). A room this sign-in can't play (a Token room, for a guest) isn't listed
			// for it, so the menu picks the first open one instead
			const pickedSignedOut = session ? undefined : selectedRoomId;
			resetPlayerState();
			if (pickedSignedOut !== undefined && auth.session) selectedRoomId = pickedSignedOut;
			if (!auth.session) untickTerms();
		}
		session = auth.session;
		noteSignedIn(auth.session?.address);
		registration = auth.registration;
		developmentTestMode = auth.developmentTestMode;
		activeDevelopmentTest = auth.activeDevelopmentTest;
		// back on the page (a reload): the last paid entry, in its room, says what became of it; and what becomes of
		// the one on screen (played, its round cancelled and refunded) comes with every session check
		if (!paidEntryAdopted && auth.session) {
			paidEntryAdopted = true;
			if (!entryIntent && auth.paidEntry) {
				entryIntent = auth.paidEntry;
				if (!registration) selectedRoomId = auth.paidEntry.roomId;
			}
		} else if (entryIntent && auth.paidEntry?.id === entryIntent.id)
			entryIntent = { ...auth.paidEntry, execution: entryIntent.execution };
		heldEntryId = auth.paidEntryHeld ? auth.paidEntry?.id : undefined;
		latestPaidEntry = auth.paidEntry;
		// a payment used, or given back: its transaction needn't be remembered on this browser any more
		if (auth.paidEntry && ["consumed", "refund_pending", "refunded"].includes(auth.paidEntry.state)) forgetEntryTransaction(auth.paidEntry.id);
		guestWins = auth.guestWins ?? [];
		walletWins = auth.walletWins ?? [];
		if (auth.notice) stickyError = { text: tr(auth.notice), until: Date.now() + 12_000 };
		// signed out again later (a wallet sign-in lasts a day), the menu puts this way of signing in first
		if (auth.session) {
			try {
				localStorage.setItem(LAST_SIGN_IN_KEY, auth.session.verified ? "wallet" : "guest");
			} catch {
				/* storage unavailable */
			}
		}
	} else {
		// couldn't tell (the server out of reach for a moment, an update going in): the last known state stays. Shown
		// as signed out, one press on "Play as guest" used to sign the wallet out and give up its place
		sessionCheckFailingSince ??= Date.now();
	}

	if (session && nameLoadedFor !== session.address) {
		const loadingFor = session.address;
		nameLoadedFor = loadingFor;
		try {
			const name = (await request<{ name: string | null }>("/profile/name")).name;
			if (session?.address === loadingFor) myName = name;
		} catch {
			// asked again at the next check (a guest is shown by its Guest name meanwhile, never its made-up address)
			if (nameLoadedFor === loadingFor) nameLoadedFor = undefined;
		}
	}
	if (!session) {
		myName = null;
		nameLoadedFor = undefined;
	}
	// a payment under way is followed (a finished one doesn't change any more)
	if (entryIntent && session && !finishedIntent(entryIntent)) {
		// (the payment flow may swap in a fresh request meanwhile: the answer about the old one doesn't bring it back)
		const asked = entryIntent.id;
		try {
			const fresh = await request<EntryPaymentIntentOwnerViewV1>(`/entry/intents/${encodeURIComponent(asked)}`);
			if (entryIntent?.id === asked) entryIntent = { ...fresh, execution: fresh.execution ?? entryIntent.execution };
		} catch (error) {
			// keep the last owner-visible state during a transient outage; a request the server no longer has (it
			// restarted) is gone. One whose payment this browser sent isn't forgotten unsaid: the payment isn't a seat,
			// it's refunded (the menu says so), and paying that room again asks first. Said only to the player it's for:
			// a payment flow still running for the one signed in before (someone else signed in since, in another tab)
			// gets a 404 under the new player, whose payment it isn't
			if ((error as { readonly status?: number }).status === 404 && entryIntent?.id === asked) {
				if (session && sentFromHere(asked) && (paymentFor === undefined || paymentFor === session.address))
					noteLostPayment({
						intentId: asked,
						roomId: entryIntent.roomId,
						player: session.address,
						payer: entryIntent.payer ?? entryIntent.execution?.authorization.payer.toLowerCase() ?? session.address,
						amountAtomic: entryIntent.amountAtomic,
						tokenDecimals: entryIntent.asset.tokenDecimals,
						noticedAt: Date.now(),
					});
				entryIntent = undefined;
			}
		}
	}
	// a lost request the server knows again after all (it was only out of reach): no longer said to be lost
	if (latestPaidEntry && lostPayments().some(({ intentId }) => intentId === latestPaidEntry?.id))
		keepLostPayments(lostPayments().filter(({ intentId }) => intentId !== latestPaidEntry?.id));
	void refreshLobby();
	renderPayouts();

	// the wait just started for a round you're registered in (in your room): chime
	const nextState = myNextRound()?.state;
	if (
		registration &&
		registration.state !== "playing" &&
		nextState === "countdown" &&
		lastNextState !== "countdown"
	)
		playReadyChime();
	lastNextState = nextState;

	// admitted: enter automatically after a short countdown, unless cancelled (never once out of the round: a reload
	// forgets which rounds it entered, and it would try to enter one they're already out of)
	if (
		registration &&
		inRoundToPlay() &&
		!autoJoinHandled.has(registration.roundId) &&
		!autoJoin
	) {
		autoJoin = {
			roundId: registration.roundId,
			at: Date.now() + AUTO_JOIN_DELAY_MS,
		};
		playAdmittedCall();
		if (await inGame())
			toast(
				t("The public round is starting: leaving the test battle in <n> seconds.", { n: Math.round(AUTO_JOIN_DELAY_MS / 1000) }),
				AUTO_JOIN_DELAY_MS,
			);
	}
	render();
};

/** 1-second UI clock: countdowns and the auto-join trigger */
const enterAdmittedRound = async (): Promise<void> => {
	// coming from a test battle (or spectating one): leave it first, then join the public round
	try {
		const { Game } = await import("./game");
		if (Game.gameStarted) await Game.endGame();
	} catch {
		/* not in a game */
	}
	render(t("Entering the battle\u2026"));
	document.getElementById("btn-play-solo")?.click();
};

const tickLobby = (): void => {
	if (lobby?.state === "countdown" || lobby?.activeRound) renderLobby();
	// (the rooms' countdowns, in the picker and the list)
	tickRoomStates();
	if (autoJoin && Date.now() >= autoJoin.at) {
		autoJoinHandled.add(autoJoin.roundId);
		autoJoin = undefined;
		void enterAdmittedRound();
		return;
	}
	const s = myNextRound()?.state;
	if (autoJoin || (registration && s === "countdown")) render();
};

/** the 18+ terms box, ticked: both ways in need it */
const requireTerms = (): void => {
	const terms = document.getElementById(
		"wallet-terms",
	) as HTMLInputElement | null;
	if (!terms?.checked)
		throw new Error(t("Tick the box (18+ and the current rules and terms) to continue"));
};

/** a guest: no wallet, the free room and the Training Range (the server makes it, and names it Guest-1234) */
const playAsGuest = async (): Promise<void> => {
	requireTerms();
	authEpoch++;
	try {
		await request<SessionResponse>("/auth/guest", {
			method: "POST",
			body: JSON.stringify({ termsAccepted: true }),
		});
	} catch (error) {
		// still signed in with a wallet here (this page had lost track of it): shown again as it is
		if ((error as { readonly code?: string }).code === "signed_in_with_wallet") await refresh();
		throw error;
	}
	await refresh();
};

/**
 * The SIWE message for this site, signed in the wallet and sent to `/auth/verify`: a verified session for that wallet
 * (no earlier sign-in needed). The text says it authorizes no token transaction.
 */
const signWithWallet = async (
	provider: Eip1193Provider,
	address: string,
	statement: string,
	abandoned: () => boolean = () => false,
): Promise<{ readonly guestLeftQueue?: boolean; readonly queueMovedToWallet?: boolean; readonly guestLeftRound?: boolean } | undefined> => {
	const chainHex = await provider.request<string>({ method: "eth_chainId" });
	const chainId = Number.parseInt(chainHex, 16);
	if (!Number.isSafeInteger(chainId) || chainId < 1)
		throw new Error(t("The connected wallet returned an invalid chain"));
	const nonce = await request<AuthNonce>("/auth/nonce");
	const issuedAt = new Date();
	const message = [
		`${nonce.domain} wants you to sign in with your Ethereum account:`,
		// (written checksummed, as sign-in with Ethereum asks: wallets that read sign-in messages show theirs, with its
		// warning when the site isn't the one named; the server takes either)
		isAddress(address, { strict: false }) ? getAddress(address) : address,
		"",
		`${statement} This signature does not authorize a token transaction.`,
		"",
		`URI: ${window.location.origin}`,
		"Version: 1",
		`Chain ID: ${chainId}`,
		`Nonce: ${nonce.nonce}`,
		`Issued At: ${issuedAt.toISOString()}`,
		`Expiration Time: ${new Date(nonce.expiresAt).toISOString()}`,
	].join("\n");
	if (abandoned()) return undefined;
	walletStep = "sign";
	render();
	const signature = await provider.request<string>({
		method: "personal_sign",
		params: [message, address],
	});
	// cancelled while the wallet was open: its signature is never sent
	if (abandoned()) return undefined;
	walletStep = "check";
	authEpoch++;
	render();
	return request<{ readonly guestLeftQueue?: boolean; readonly queueMovedToWallet?: boolean; readonly guestLeftRound?: boolean }>("/auth/verify", {
		method: "POST",
		body: JSON.stringify({ message, signature, termsAccepted: true }),
	});
};

/**
 * While the wallet app is asked something: where to find it. It doesn't always pop up (the request can wait behind its
 * icon in the browser's toolbar), and its Sign button sits at the bottom of its window.
 */
const walletWaitHint = (): string => {
	if (walletStep === "check") return t("Signed. Checking your signature…");
	const slow = walletPending !== undefined && Date.now() - walletPending.since > 6_000;
	const ask =
		walletStep === "connect"
			? t("Your wallet asks to connect to this site: choose your account and press Connect.")
			: t("Your wallet shows a sign-in message: scroll to the bottom and press Sign (it's free: no payment, nothing is sent).");
	return slow
		? sentences(ask, t("Nothing opened? Click your wallet's icon in the browser's toolbar (MetaMask: the fox, top right): the request is waiting there."))
		: `${ask}`;
};

/** what the wallet answered, in words: cancelled, already asking, or its own message */
const walletErrorText = (error: unknown): string => {
	if (refusedInWallet(error)) return t("You cancelled in your wallet. Press Sign in with wallet to try again.");
	if (walletCodesOf(error).includes(-32002))
		return t("Your wallet already has a request waiting: open it from the browser's toolbar (MetaMask: the fox, top right) and finish or cancel it, then try again.");
	const words = wordsOf(error);
	return words && words !== "[object Object]" ? words : t("Signing in with your wallet didn't work: try again");
};

/**
 * Signing in with the wallet itself: from signed out, or a guest moving to their wallet (the wallet starts fresh: the
 * guest ends, and gives up a place it had in a queue). Only this click opens a wallet. Two steps in the wallet: share
 * the account (once per site), then sign the sign-in message.
 */
const signInWithWallet = async (): Promise<void> => {
	if (!session) requireTerms();
	if (guestHasPaidPlace()) throw new Error(sentences(guestPaidPlace(), t("Sign in with your wallet after the round.")));
	// a guest still in a running round: the round goes on without it (it used to be refused until the round ended,
	// even once the guest was knocked out of it)
	if (
		session?.guest &&
		inRoundToPlay() &&
		!window.confirm(
			t("Your guest is still in a running round. Sign in with your wallet anyway? The round goes on without your guest; your wallet can play the next one."),
		)
	)
		return;
	// (signed in with a wallet, the guest ends: a win it hasn't said where to send can't be chosen any more)
	if (!giveUpUnchosenWin(t("Signed in with your wallet (your guest ends there)"), t("Sign in with your wallet anyway?"))) return;
	const attempt = ++walletAttempt;
	/** Cancel was pressed (or another try started) since: whatever this try gets back is ignored */
	const abandoned = (): boolean => attempt !== walletAttempt;
	walletPending = { since: Date.now() };
	walletStep = "connect";
	render();
	// the hint grows (where to find the wallet) if it hasn't answered after a few seconds
	const hintTimer = setInterval(() => render(), 1_000);
	try {
		const provider = await walletProvider();
		if (abandoned()) return;
		const accounts = await provider.request<readonly string[]>({
			method: "eth_requestAccounts",
		});
		if (abandoned()) return;
		const address = accounts[0]?.toLowerCase();
		if (!address)
			throw new Error(t("Your wallet didn't share an account: unlock it and try again"));
		const wasGuest = session?.guest === true;
		const signedIn = await signWithWallet(provider, address, "Sign in to TRENCHIO with your wallet.", abandoned);
		if (!signedIn) return;
		walletPending = undefined;
		await refresh();
		// what became of a guest's place, for a few seconds. A plain "Signed in with your wallet." isn't said: the panel
		// shows the wallet, and the notice hid what it says about the room picked for 8 seconds
		const notice = signedIn.queueMovedToWallet
			? t("Signed in with your wallet: your place in the queue is your wallet's now.")
			: signedIn.guestLeftQueue
				? t("Signed in with your wallet. Your guest's place in the queue was given up (it couldn't move to your wallet).")
				: signedIn.guestLeftRound
					? t("Signed in with your wallet. Your guest's round goes on without it: register for the next round.")
					: wasGuest
						? t("Signed in with your wallet: every room is open to you now.")
						: undefined;
		if (notice) stickyError = { text: notice, until: Date.now() + 5_000 };
		render(notice);
	} catch (error) {
		if (abandoned()) return;
		throw new Error(walletErrorText(error));
	} finally {
		clearInterval(hintTimer);
		if (!abandoned()) walletPending = undefined;
	}
};

/** Cancel while the wallet is asked: stop waiting for it (its window, if still open, is closed there) */
const cancelWalletSignIn = (): void => {
	walletAttempt++;
	walletPending = undefined;
	renderError(t("Stopped waiting for your wallet. If its window is still open, close it there. Press Sign in with wallet to try again."));
};

const injectedProvider = (): Eip1193Provider | undefined =>
	(
		window as Window & {
			readonly ethereum?: Eip1193Provider;
		}
	).ethereum;

const walletProvider = async (): Promise<Eip1193Provider> => {
	const injected = injectedProvider();
	if (injected) return injected;
	const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim();
	if (!projectId)
		throw new Error(
			t("No wallet was found in this browser. On a computer, use the browser with your wallet extension (MetaMask, Rabby…); on a phone, open the site in your wallet app's own browser."),
		);
	// one connection for the page (a new one each time opened another link to the relay)
	if (walletConnectProvider) {
		if (!walletConnectProvider.connected) await walletConnectProvider.connect();
		return walletConnectProvider;
	}
	const { default: EthereumProvider } = await import(
		"@walletconnect/ethereum-provider"
	);
	const provider = await EthereumProvider.init({
		projectId,
		// asked for, not required: a phone wallet that doesn't have Robinhood Chain refused a connection that required it (a
		// wallet sign-in included); paying asks the wallet to add it and switch to it (see walletPayer)
		optionalChains: [4663, 46630],
		// what the page reads through the connection (a transaction's receipt, an allowance) goes to Robinhood Chain's own
		// public address: WalletConnect's default one may not serve this chain
		rpcMap: { 4663: chainToAdd(4663)?.rpcUrls[0] ?? "https://rpc.mainnet.chain.robinhood.com", 46630: chainToAdd(46630)?.rpcUrls[0] ?? "https://rpc.testnet.chain.robinhood.com" },
		showQrModal: true,
		// its window over every one of the menu's, the store's too (z-index 20000): it opened under the store, which took
		// every click, its purchase left on "Paying…" (0.94 audit). AppKit takes WalletConnect's old name for it
		qrModalOptions: { themeVariables: { "--wcm-z-index": "40000" } },
		metadata: {
			name: "TRENCHIO",
			description: "Sign in to TRENCHIO",
			url: window.location.origin,
			// a picture first: most wallet apps don't draw an SVG icon
			icons: [`${window.location.origin}/img/logos/icon_512.png`, `${window.location.origin}/img/logos/project_mark.svg`],
		},
	});
	if (!provider.connected) await provider.connect();
	walletConnectProvider = provider;
	return provider;
};
/** the WalletConnect connection made on this page, if one was (ended when the player signs out) */
let walletConnectProvider: (Eip1193Provider & { readonly connected: boolean; connect(): Promise<unknown>; disconnect(): Promise<void> }) | undefined;

/**
 * WalletConnect's own record of its connections on this browser (kept in IndexedDB), emptied: a connection to a phone's
 * wallet made by an earlier player isn't picked up again by the next one. Nothing is made when there's none.
 */
const clearWalletConnectStorage = (): Promise<void> =>
	new Promise((resolve) => {
		try {
			const opening = indexedDB.open("WALLET_CONNECT_V2_INDEXED_DB");
			// it didn't exist: nothing to clear (and it isn't made just for that)
			opening.onupgradeneeded = () => opening.transaction?.abort();
			opening.onerror = () => resolve();
			opening.onblocked = () => resolve();
			opening.onsuccess = () => {
				const db = opening.result;
				const finish = () => {
					db.close();
					resolve();
				};
				try {
					if (!db.objectStoreNames.contains("keyvaluestorage")) return finish();
					const clearing = db.transaction("keyvaluestorage", "readwrite");
					clearing.objectStore("keyvaluestorage").clear();
					clearing.oncomplete = finish;
					clearing.onerror = finish;
					clearing.onabort = finish;
				} catch {
					finish();
				}
			};
		} catch {
			resolve();
		}
	});

interface WalletTransactionReceipt {
	readonly status?: string;
	readonly blockNumber?: string;
}

/** a transaction that's in a block and failed there (the blockchain refused it: nothing it would have done happened) */
class TransactionReverted extends Error {
	readonly reverted = true;
}

/**
 * a transaction never sent: the wallet's network, checked again before sending, isn't the one it's for (its words end
 * "Nothing was sent." in English; in another language they're the phrasebook's, so the store's payment knows it by this)
 */
class NothingSent extends Error {}

const waitForWalletTransaction = async (
	provider: Eip1193Provider,
	hash: string,
): Promise<void> => {
	const deadline = Date.now() + 3 * 60_000;
	let unanswered = 0;
	while (Date.now() < deadline) {
		let receipt: WalletTransactionReceipt | null;
		try {
			receipt = await provider.request<WalletTransactionReceipt | null>({
				method: "eth_getTransactionReceipt",
				params: [hash],
			});
			unanswered = 0;
		} catch (error) {
			// the wallet's connection to the chain didn't answer this time ("Failed to fetch"): the chain's public address
			// (or the game server) is asked instead, and the wallet again a few times. It's no sign the transaction failed
			// (taken for one, its hash was forgotten, and the next press paid again)
			const state = /^0x[0-9a-fA-F]{64}$/u.test(hash) ? await chainReadsNow()?.transaction(hash as `0x${string}`).catch(() => undefined) : undefined;
			if (state === "success") return;
			if (state === "failed") throw new TransactionReverted(t("The transaction failed on the blockchain: nothing was paid. Try again."));
			if (state === undefined && ++unanswered >= 5)
				throw new Error(t("Your wallet can't reach the blockchain right now (<error>): your transaction may still go through. Wait a minute, then press the pay button again: nothing is sent twice.", { error: wordsOf(error) }));
			await new Promise((resolve) => setTimeout(resolve, 2_000));
			continue;
		}
		if (receipt) {
			if (receipt.status !== "0x1")
				throw new TransactionReverted(t("The transaction failed on the blockchain: nothing was paid. Try again."));
			return;
		}
		await new Promise((resolve) => setTimeout(resolve, 1_500));
	}
	throw new Error(
		t("Your payment is still being confirmed. Wait a few seconds, then press the pay button again."),
	);
};

/** what a wallet allows the vault to take from it (undefined when it can't be asked) */
const allowanceOf = async (payer: Payer, token: `0x${string}`, spender: `0x${string}`): Promise<bigint | undefined> => {
	try {
		const usdg = entryMethods?.usdg;
		const data = encodeFunctionData({ abi: allowanceAbi, functionName: "allowance", args: [payer.address, spender] });
		const answer = await (await walletProvider()).request<string>({ method: "eth_call", params: [{ to: token, data }, "latest"] }).catch(() => undefined);
		if (typeof answer === "string" && /^0x[0-9a-fA-F]+$/u.test(answer)) return BigInt(answer);
		// the wallet's connection didn't answer: the USDG rooms' allowance is read on the chain's public address instead
		const usdgVault = usdg && usdg.tokenAddress.toLowerCase() === token.toLowerCase() && usdg.escrowAddress?.toLowerCase() === spender.toLowerCase();
		return usdgVault ? await chainReadsNow()?.usdgAllowance(payer.address) : undefined;
	} catch {
		return undefined;
	}
};

/** a transaction a wallet is asked to send (`value`: ETH sent with it, in wei) */
interface WalletTransaction {
	readonly to: `0x${string}`;
	readonly data: `0x${string}`;
	readonly value?: bigint;
}

/**
 * The wallet a payment (or a payout choice) comes from: a wallet app on Robinhood Chain (the player's own, signed in,
 * or any for a guest).
 */
interface Payer {
	readonly kind: "wallet";
	readonly address: `0x${string}`;
	/** how messages name it */
	readonly label: string;
	/** on the chain the payment is made on (a wallet app may be on another) */
	prepare(chainId: number): Promise<void>;
	sign(message: string): Promise<`0x${string}`>;
	/** sends one transaction: its hash once it's sent */
	submit(transaction: WalletTransaction): Promise<`0x${string}`>;
	/** waits until a transaction is in a block, and succeeded */
	wait(hash: string): Promise<void>;
}

/**
 * the codes a wallet's error carries, its own and those it wraps (MetaMask on a phone wraps "unknown chain" and "you
 * refused" in an internal error; others put the cause elsewhere)
 */
const walletCodesOf = (error: unknown): readonly unknown[] => {
	const codes: unknown[] = [];
	let cursor: unknown = error;
	for (let depth = 0; depth < 5 && cursor && typeof cursor === "object"; depth++) {
		const wrapped = cursor as { readonly code?: unknown; readonly data?: { readonly originalError?: unknown; readonly cause?: unknown }; readonly cause?: unknown; readonly error?: unknown };
		codes.push(wrapped.code);
		cursor = wrapped.data?.originalError ?? wrapped.data?.cause ?? wrapped.cause ?? wrapped.error;
	}
	return codes;
};

/** a wallet's error in its own words (some wallets throw plain objects, not errors) */
const wordsOf = (error: unknown): string =>
	error instanceof Error ? error.message : typeof (error as { readonly message?: unknown } | undefined)?.message === "string" ? (error as { readonly message: string }).message : String(error);

/**
 * what's said when the player said no in their wallet: nothing was paid; and when a payment request is open (made before
 * the approval or the payment was refused), that it can still be paid, and when it runs out (the room can't be changed
 * until then: its ticket stays good in the vault). `saidNo`: it was the menu's own question they said no to
 */
const cancelledInWalletText = (saidNo = false): string => {
	const open = entryIntent?.state === "pending" && !finishedIntent(entryIntent) ? entryIntent : undefined;
	if (!open)
		return saidNo
			? t("You said no: nothing was paid. Press the button again when you're ready.")
			: t("You cancelled in your wallet: nothing was paid. Press the button again when you're ready.");
	const lapses = new Date(open.expiresAt + 60_000 - clockOffset).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
	return saidNo
		? t("You said no: nothing was paid. Press Pay & join to pay this entry after all; its request runs out at <time>, and until then the menu stays on this room.", { time: lapses })
		: t("You cancelled in your wallet: nothing was paid. Press Pay & join to pay this entry after all; its request runs out at <time>, and until then the menu stays on this room.", { time: lapses });
};

/** the player refused in their wallet (any wallet, however it says it) */
const refusedInWallet = (error: unknown): boolean => {
	const codes = walletCodesOf(error);
	return codes.includes(4001) || codes.includes("ACTION_REJECTED") || /user (rejected|denied|cancell?ed)|rejected by (the )?user|request rejected/iu.test(wordsOf(error));
};

const chainNameOf = (chainId: number): string => (chainId === 4663 ? "Robinhood Chain" : chainId === 46630 ? "Robinhood Chain Testnet" : t("the network <id>", { id: chainId }));

/**
 * what a wallet app is asked to add when it doesn't know the chain the rooms are paid on (Robinhood Chain is new: most
 * wallets don't have it yet). Its own public address and explorer (docs.robinhood.com/chain/connecting), or for another
 * chain the address the game server names for it
 */
const chainToAdd = (chainId: number) => {
	const known = (
		{
			4663: { name: "Robinhood Chain", rpc: "https://rpc.mainnet.chain.robinhood.com", explorer: "https://robinhoodchain.blockscout.com" },
			46630: { name: "Robinhood Chain Testnet", rpc: "https://rpc.testnet.chain.robinhood.com", explorer: "https://explorer.testnet.chain.robinhood.com" },
		} as Readonly<Record<number, { readonly name: string; readonly rpc: string; readonly explorer: string }>>
	)[chainId];
	const usdg = entryMethods?.usdg;
	const rpc = known?.rpc ?? (usdg?.chainId === chainId ? usdg.rpcUrl : undefined);
	if (!rpc) return undefined;
	return {
		chainId: `0x${chainId.toString(16)}`,
		chainName: known?.name ?? `Chain ${chainId}`,
		nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
		rpcUrls: [rpc],
		...(known ? { blockExplorerUrls: [known.explorer] } : {}),
	};
};

/** the wallet doesn't know that chain (MetaMask's 4902, or another wallet's words for it) */
const unknownChain = (error: unknown): boolean =>
	walletCodesOf(error).includes(4902) ||
	/unrecognized chain|unknown chain|chain (id )?.*not (been )?(added|supported|configured|found)|wallet_addEthereumChain|add (the|this) (chain|network)|no such chain/iu.test(wordsOf(error));

/**
 * a wallet app (MetaMask, Rabby, WalletConnect…): `expected` is the one it must be (the player's own, or the payer),
 * `why` which wallet that is, in words ("the wallet that paid your entry": said in brackets after its address).
 * `words`: what is being paid (an entry, the store's payment) and where its notices go (the wallet panel, the store)
 */
const walletPayer = async (expected?: string, why = "", words: { readonly what?: string; readonly notify?: (text: string) => void } = {}): Promise<Payer> => {
	const what = words.what ?? t("the entry");
	const notify = words.notify ?? render;
	const provider = await walletProvider();
	const accounts = await provider.request<readonly string[]>({ method: "eth_requestAccounts" });
	const address = accounts[0]?.toLowerCase() as `0x${string}` | undefined;
	if (!address) throw new Error(t("Your wallet didn't share an account: unlock it and try again"));
	if (expected && address !== expected.toLowerCase())
		throw new Error(
			why
				? t("Switch your wallet to <wallet> (<why>), then try again", { wallet: shorten(expected), why })
				: t("Switch your wallet to <wallet>, then try again", { wallet: shorten(expected) }),
		);
	/** the chain this wallet pays on (set by prepare): every transaction is sent there, or not at all */
	let paysOn: number | undefined;
	const onChain = async (chainId: number): Promise<boolean> => {
		const current = await provider.request<string | number>({ method: "eth_chainId" });
		return Number(current) === chainId;
	};
	/** on that chain within a moment (a wallet that has just switched may say so a moment later) */
	const reachedChain = async (chainId: number): Promise<boolean> => {
		for (let look = 0; look < 6; look++) {
			if (await onChain(chainId).catch(() => false)) return true;
			await new Promise((resolve) => setTimeout(resolve, 400));
		}
		return false;
	};
	return {
		kind: "wallet",
		address,
		label: t("your wallet (<address>)", { address: shorten(address) }),
		async prepare(chainId) {
			paysOn = chainId;
			if (await onChain(chainId)) return;
			const name = chainNameOf(chainId);
			const chainHex = `0x${chainId.toString(16)}`;
			try {
				await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: chainHex }] });
			} catch (error) {
				if (refusedInWallet(error))
					throw new Error(t("Your wallet stayed on another network: <what> is paid on <chain>. Press the button again and accept the switch. Nothing was paid.", { what, chain: name }));
				const add = chainToAdd(chainId);
				// a wallet that doesn't know Robinhood Chain yet (most don't): it's asked to add it (free), then to switch
				if (!add || !unknownChain(error))
					throw new Error(t("Your wallet couldn't switch to <chain> (<error>): switch it there yourself, then press the button again. Nothing was paid.", { chain: name, error: wordsOf(error) }));
				notify(t("Your wallet doesn't know <chain> yet: it asks you to add it (free, nothing is paid).", { chain: add.chainName }));
				try {
					await provider.request({ method: "wallet_addEthereumChain", params: [add] });
				} catch (addError) {
					if (refusedInWallet(addError))
						throw new Error(t("<chain> wasn't added to your wallet: <what> is paid there. Press the button again and accept adding it. Nothing was paid.", { chain: add.chainName, what }));
					throw new Error(
						t("Your wallet couldn't add <chain> (<error>): add it yourself (network ID <id>, RPC <rpc>, currency ETH), then press the button again. Nothing was paid.", {
							chain: add.chainName,
							id: chainId,
							rpc: add.rpcUrls[0],
							error: wordsOf(addError),
						}),
					);
				}
				// (most wallets switch to a network they've just added; some only add it)
				if (!(await reachedChain(chainId)))
					await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: chainHex }] }).catch((switchError: unknown) => {
						throw new Error(
							refusedInWallet(switchError)
								? t("Your wallet stayed on another network: <what> is paid on <chain>. Press the button again and accept the switch. Nothing was paid.", { what, chain: name })
								: t("Your wallet couldn't switch to <chain> (<error>): switch it there yourself, then press the button again. Nothing was paid.", { chain: name, error: wordsOf(switchError) }),
						);
					});
			}
			if (!(await reachedChain(chainId)))
				throw new Error(t("Your wallet is still on another network: switch it to <chain>, then press the button again. Nothing was paid.", { chain: name }));
		},
		sign: (message) => provider.request<`0x${string}`>({ method: "personal_sign", params: [message, address] }),
		async submit(transaction) {
			// switched to another network since (in the wallet, while the menu asked something): nothing is sent there. A
			// wallet that doesn't say which network it's on isn't taken to be on the right one (it used to be)
			if (paysOn !== undefined) {
				const on = await onChain(paysOn).catch(() => undefined);
				if (on === undefined)
					throw new NothingSent(t("Your wallet didn't say which network it's on: check that it's on <chain>, then press the button again. Nothing was sent.", { chain: chainNameOf(paysOn) }));
				if (!on)
					throw new NothingSent(t("Your wallet is on another network now: switch it back to <chain>, then press the button again. Nothing was sent.", { chain: chainNameOf(paysOn) }));
			}
			return provider.request<`0x${string}`>({
				method: "eth_sendTransaction",
				// (the network it's for, too: a wallet that changed network in between refuses it, never sends it elsewhere)
				params: [{ from: address, to: transaction.to, data: transaction.data, ...(transaction.value ? { value: `0x${transaction.value.toString(16)}` } : {}), ...(paysOn !== undefined ? { chainId: `0x${paysOn.toString(16)}` } : {}) }],
			});
		},
		wait: (hash) => waitForWalletTransaction(provider, hash),
	};
};

/**
 * The paying wallet's proof that it pays this player's entries (free: it moves no money). Not needed when the player's
 * own signed-in wallet pays; kept while valid, so asking again (a fresh request) doesn't ask to sign again.
 */
const proofFor = async (payer: Payer): Promise<PayerProofV1 | undefined> => {
	if (!session || payer.address === session.address) return undefined;
	const player = session.address;
	if (payerProof && payerProof.payer === payer.address && payerProof.player === player && payerProof.validUntil - serverNow() > 2 * 60_000)
		return { payer: payerProof.payer, message: payerProof.message, signature: payerProof.signature };
	// dated by the server's clock (see clockOffset): the server takes it for up to 15 minutes ahead
	const validUntil = serverNow() + 10 * 60_000;
	const message = payerProofMessage({
		site: window.location.host,
		player,
		playerName: nameInMessages(),
		payer: payer.address,
		validUntil,
	});
	render(t("Sign the message in your wallet: it says <payer> pays your entry. It's free and moves no money.", { payer: payer.label }));
	const signature = await payer.sign(message);
	stillPlayer(player);
	payerProof = { payer: payer.address, message, signature, player, validUntil };
	return { payer: payer.address, message, signature };
};

/** asks for a payment request for the selected room, paid from this wallet */
const requestPayment = async (roomId: RoomId, payer: Payer): Promise<EntryPaymentIntentOwnerViewV1> => {
	const proof = await proofFor(payer);
	try {
		return await request<EntryPaymentIntentOwnerViewV1>("/entry/intents", {
			method: "POST",
			body: JSON.stringify({ version: 1, roomId, ...(proof ? { payer: proof } : {}) }),
		});
	} catch (error) {
		// a proof the server didn't take isn't kept: the next press signs a new one
		if ((error as { readonly code?: string }).code === "payer_proof_refused") payerProof = undefined;
		throw error;
	}
};

/** the wallet the payment on screen is paid from: the one that started it on this page, else found again */
let activePayer: Payer | undefined;
const payerForIntent = (intent: EntryPaymentIntentOwnerViewV1): Promise<Payer> => {
	const wanted = intent.payer ?? session?.address;
	if (activePayer && activePayer.address === wanted) return Promise.resolve(activePayer);
	return walletPayer(wanted, intent.payer ? t("the wallet this payment is paid from") : "");
};

/**
 * How the player pays: a wallet app on Robinhood Chain (their own, signed in; a guest's, any). The Token room is paid
 * from a signed-in player's own wallet.
 */
const choosePayer = (roomId: RoomId): Promise<Payer | undefined> => {
	if (!session) return Promise.resolve(undefined);
	const own = session.verified && !session.guest ? session.address : undefined;
	if (isStakeRoom(roomId) && !own) render(t("Connect a wallet holding USDG on Robinhood Chain (MetaMask, Rabby…): it pays your entry, and gets your winnings unless you choose another address."));
	return walletPayer(own);
};

/**
 * Whether this request's payment is in the vault: the server looks, and puts the player in the round if it is.
 * Asked when sending the payment failed without saying whether it went out (`waitMs`: a moment given first, for one
 * still on its way), so nothing is ever sent twice and a payment that went through is never called unpaid.
 */
const paidAlready = async (intentId: string, waitMs = 0): Promise<boolean> => {
	if (waitMs) await new Promise((resolve) => setTimeout(resolve, waitMs));
	try {
		const checked = await request<EntryPaymentIntentOwnerViewV1>(`/entry/intents/${encodeURIComponent(intentId)}/confirm`, {
			method: "POST",
			body: JSON.stringify({ checkVault: true }),
		});
		if (entryIntent?.id === intentId) entryIntent = { ...checked, execution: checked.execution ?? entryIntent.execution };
		if (checked.state === "consumed" || checked.state === "confirmed") return true;
	} catch {
		/* asked again by the server itself before the round starts */
	}
	return false;
};

/**
 * The question asked before an entry is paid: the exact price, from which wallet, into which room, and what happens to
 * it. `ticketSigned`: the game stamps the ticket (an older server had the paying wallet sign it too)
 */
const payQuestion = (input: { readonly price: string; readonly payer: Payer; readonly roomId: RoomId; readonly ticketSigned: boolean }): string => {
	const definition = roundStatus?.rooms.find(({ definition }) => definition.id === input.roomId)?.definition;
	const guest = session?.guest === true;
	const refundTo = guest || input.payer.address !== session?.address
		? t("Refunds go back to the wallet that paid (<wallet>).", { wallet: shorten(input.payer.address) })
		: t("Refunds go back to your wallet.");
	// a Token room's entry is burned, not paid out: its prize is the fee pool's (said as the USDG rooms say where theirs goes)
	const burned = definition?.entryFundsPolicy?.kind === "burn" || (definition?.entryFundsPolicy === undefined && isTokenRoomId(input.roomId));
	const toWinner = definition?.entryFundsPolicy?.kind === "winner_pot";
	return [
		t("Pay exactly <price> from <payer> to enter the <room> room?", { price: input.price, payer: input.payer.label, room: trMaybe(definition?.displayName) ?? input.roomId }),
		sentences(
			t("Once paid, your seat is kept: you can leave (and get your entry back) only while you're the only one in the round."),
			paidSeatRule(),
			refundTo,
			toWinner && (bettingPrize(input.roomId) || t("The winner gets the pot, less the vault's betting fee if it takes one.")),
			toWinner && guest && t("If you win, you choose where yours goes."),
			t("Real players only, no bots."),
		),
		...(burned ? [t("The <price> is burned once the round is final; the winner's prize comes from the fee pool.", { price: input.price })] : []),
		// a guest is only this browser's sign-in (its cookie): another browser, or a window that forgets it, can't play the seat
		...(guest ? [t("Stay in this browser and window until the round is over: a private window that's closed, or cleared cookies, loses the seat.")] : []),
		input.ticketSigned
			? t("Your wallet will ask you to confirm twice: allow the exact amount, then pay.")
			: t("Your wallet will ask you to confirm a signature and twice: allow the exact amount, then pay."),
	].join("\n\n");
};

/** the price and wallet the player said yes to at this press (asked before the payment request: see act) */
interface PayConfirmed {
	readonly amountAtomic: string;
	readonly payer: `0x${string}`;
}

/** the payment flow, with what's shown of it kept while it goes through (see `paymentFor`) */
const executeLivePaidEntry = async (payer: Payer, confirmed?: PayConfirmed): Promise<void> => {
	const player = session?.address;
	paymentFor = player;
	try {
		await payLivePaidEntry(payer, confirmed);
	} finally {
		paymentFor = undefined;
		// signed in as someone else meanwhile (another tab): nothing of that payment stays on their screen
		if (session?.address !== player) entryIntent = undefined;
	}
};

/** a transaction hash that can be looked up on the chain (a smart wallet's app may give back none, or its own id) */
const traceable = (hash: string | null | undefined): hash is `0x${string}` => typeof hash === "string" && /^0x[0-9a-fA-F]{64}$/u.test(hash);

/**
 * Waits until a sent transaction is done: in a block and through (its hash looked up), or what it was for seen done on
 * the chain (the allowance in place, the vault holding the payment). A smart wallet's app may give back no hash to
 * follow, or one that isn't the transaction's. So what the transaction did is watched as well, every few seconds, for a
 * few minutes. A transaction seen failing is said at once.
 */
const sentAndDone = async (payer: Payer, hash: string | null | undefined, seenDone: () => Promise<boolean>): Promise<void> => {
	const followed: { result: "pending" | "done" | { readonly error: unknown } } = { result: "pending" };
	if (traceable(hash))
		void payer.wait(hash).then(
			() => {
				followed.result = "done";
			},
			(error: unknown) => {
				followed.result = { error };
			},
		);
	const deadline = Date.now() + 3 * 60_000;
	for (let round = 0; ; round++) {
		// (a wallet app's own transaction is in a block within seconds: what it did is asked from the second look on)
		for (let tick = 0; tick < 8 && followed.result === "pending"; tick++) await new Promise((resolve) => setTimeout(resolve, 500));
		const result = followed.result;
		if (result === "done") return;
		if (typeof result === "object" && (result.error as { readonly reverted?: boolean }).reverted === true) throw result.error;
		if ((round > 0 || !traceable(hash)) && (await seenDone().catch(() => false))) return;
		if (Date.now() > deadline)
			throw typeof result === "object" ? result.error : new Error(t("Your transaction is still being confirmed: wait a minute, then press the pay button again (nothing is sent twice)."));
	}
};

/**
 * A payment request's ticket, checked before anything is approved or paid: the vault and network it's signed for (its
 * EIP-712 domain) are the request's own, and a USDG room's coin and vault are the USDG rooms' own (as /entry/methods
 * says). One that doesn't add up is refused: the wallet would approve, and pay, some other contract
 */
const refuseMismatchedRequest = async (intent: EntryPaymentIntentOwnerViewV1): Promise<void> => {
	const execution = intent.execution;
	if (!execution) return;
	const same = (a: string | undefined, b: string | undefined): boolean => a !== undefined && b !== undefined && a.toLowerCase() === b.toLowerCase();
	const doesntAddUp = (why: string): Error =>
		new Error(t("This payment request doesn't add up (<why>), so it isn't paid: nothing was sent. Try again in a few minutes; if it keeps happening, tell the game's team.", { why }));
	if (execution.domain.chainId !== intent.asset.chainId || !same(execution.domain.verifyingContract, intent.asset.escrowAddress))
		throw doesntAddUp(t("its ticket is for another vault or network than the request"));
	if (isStakeRoom(intent.roomId)) {
		// (asked again when this page doesn't know them yet)
		if (!entryMethods?.usdg) await refreshEntryMethods();
		const usdg = entryMethods?.usdg;
		if (!usdg) throw doesntAddUp(t("the game server didn't say which vault the USDG rooms use"));
		if (usdg.chainId !== intent.asset.chainId || !same(usdg.tokenAddress, intent.asset.tokenAddress) || !same(usdg.escrowAddress, intent.asset.escrowAddress))
			throw doesntAddUp(t("its coin or vault isn't the USDG rooms' own"));
	}
};

/**
 * The payment request, read again right before its payment is sent: "paid" when a payment for it is in already
 * (another tab, or found in the vault: nothing more is sent), else the request when it can still be paid (pending, its
 * ticket good a little longer). Anything else is refused, in every room: a request the server cancelled meanwhile (its
 * round cancelled), or that ran out, or whose ticket runs out within moments (`goodUntil`: the vault refuses a late
 * ticket after its fee). `approvalKept`: the approval isn't asked again next time (the USDG rooms)
 */
const requestStillPayable = async (intentId: string, goodUntil: number, approvalKept: boolean): Promise<EntryPaymentIntentOwnerViewV1 | "paid"> => {
	let fresh: EntryPaymentIntentOwnerViewV1;
	try {
		fresh = await request<EntryPaymentIntentOwnerViewV1>(`/entry/intents/${encodeURIComponent(intentId)}`);
	} catch (error) {
		throw new Error(
			(error as { readonly status?: number }).status === 404
				? t("This payment request is gone (the game server restarted?): nothing was paid. Press the pay button again for a fresh one.")
				: t("The game server didn't answer right before the payment (<error>): nothing was paid. Press the pay button again in a moment.", { error: tr(wordsOf(error)) }),
		);
	}
	if (entryIntent?.id === intentId) entryIntent = { ...fresh, execution: fresh.execution ?? entryIntent.execution };
	// (the approval kept: said with each refusal, as the next press asks only for the payment)
	if (fresh.state === "confirming" || fresh.state === "confirmed" || fresh.state === "consumed") return "paid";
	if (fresh.state === "cancelled")
		throw new Error(
			approvalKept
				? t("This payment request was cancelled by the game before it was paid (its round was cancelled, say): nothing was paid. Press the pay button again to join the next round (your approval stays: only the payment is asked then).")
				: t("This payment request was cancelled by the game before it was paid (its round was cancelled, say): nothing was paid. Press the pay button again to join the next round."),
		);
	if (fresh.state === "refund_pending" || fresh.state === "refunded")
		throw new Error(
			approvalKept
				? t("That round was cancelled, and this request's payment is refunded to the wallet that paid it: nothing more was paid. Press the pay button again to join the next round (your approval stays: only the payment is asked then).")
				: t("That round was cancelled, and this request's payment is refunded to the wallet that paid it: nothing more was paid. Press the pay button again to join the next round."),
		);
	if (fresh.state !== "pending" || serverNow() > Math.min(goodUntil, fresh.expiresAt) - 20_000)
		throw new Error(
			approvalKept
				? t("This payment request ran out before it could be paid: nothing was paid. Press the pay button again for a fresh one (your approval stays: only the payment is asked then).")
				: t("This payment request ran out before it could be paid: nothing was paid. Press the pay button again for a fresh one."),
		);
	return fresh;
};

const payLivePaidEntry = async (payer: Payer, confirmed?: PayConfirmed): Promise<void> => {
	if (!session || !entryIntent?.execution)
		throw new Error(
			t("This payment intent has no escrow execution authorization"),
		);
	const player = session.address;
	const storedTransaction = (id: string): string | null => {
		try {
			return localStorage.getItem(`trenchio.entryTransaction.${id}`);
		} catch {
			return null; // storage unavailable
		}
	};
	let entryTransactionHash = storedTransaction(entryIntent.id);
	// a request about to be paid: its ticket checked against it first, before the wallet is even asked to switch networks
	if (!entryTransactionHash) await refuseMismatchedRequest(entryIntent);
	await payer.prepare(entryIntent.asset.chainId);
	// not paid yet, and too little time left on the request to pay it (approving and paying take a minute or two):
	// a fresh one first (the old one can't be paid as well: the vault takes one payment per player per round)
	if (
		!entryTransactionHash &&
		entryIntent.expiresAt - serverNow() < Math.min(120_000, (entryIntent.expiresAt - entryIntent.createdAt) / 2)
	) {
		entryIntent = await requestPayment(entryIntent.roomId, payer);
		if (!entryIntent.execution)
			throw new Error(t("This payment request can't be paid from here: press the button again"));
		entryTransactionHash = storedTransaction(entryIntent.id);
		if (!entryTransactionHash) {
			await refuseMismatchedRequest(entryIntent);
			await payer.prepare(entryIntent.asset.chainId);
		}
	}
	// the vault takes the payment only from the wallet its ticket names
	if (entryIntent.execution.authorization.payer.toLowerCase() !== payer.address)
		throw new Error(t("This payment request is paid from <wallet>: pay it from that wallet, or wait a few minutes for it to run out", { wallet: shorten(entryIntent.execution.authorization.payer) }));
	// (kept: the request on screen can be swapped or dropped by a session check while a wallet takes minutes)
	const intentId = entryIntent.id;
	const intentExpiresAt = entryIntent.expiresAt;
	const transactionStorageKey = `trenchio.entryTransaction.${intentId}`;
	if (!entryTransactionHash) {
		const execution = entryIntent.execution;
		// what the ticket lets the vault take is what the request says, to the last unit (and what's approved, below)
		if (execution.authorization.amount !== entryIntent.amountAtomic || execution.authorization.payer.toLowerCase() !== payer.address)
			throw new Error(t("This payment request doesn't add up (its ticket names another amount or wallet): nothing was paid. Press the button again for a fresh one."));
		const room = roundStatus?.rooms.find(
			({ definition }) => definition.id === entryIntent?.roomId,
		);
		// the exact amount the request asks (the room's label could be from before its price changed)
		const unit = labelCoin(room?.entryLabel) ?? (isStakeRoom(entryIntent.roomId) ? "USDG" : t("tokens"));
		const price = `${exactCoinText(entryIntent.amountAtomic, entryIntent.asset.tokenDecimals)} ${unit}`;
		// asked at the press already (before the request was made), for this exact price from this wallet: not asked twice
		const saidYes = confirmed?.amountAtomic === entryIntent.amountAtomic && confirmed.payer === payer.address;
		if (!saidYes && !window.confirm(payQuestion({ price, payer, roomId: entryIntent.roomId, ticketSigned: execution.signature !== undefined })))
			throw new Error(cancelledInWalletText(true));
		// the game stamps the ticket (the vault only accepts its official stamp); older servers asked the player
		const signature = execution.signature ?? (await signTicketInWallet(payer.address, execution));
		stillPlayer(player);
		const amount = BigInt(entryIntent.amountAtomic);
		const { tokenAddress, escrowAddress } = entryIntent.asset;
		// (the USDG rooms: the Token room is paid as it always was)
		const usdgRoom = isStakeRoom(entryIntent.roomId);
		// allowed already (an earlier try that stopped after its approval): only the payment is sent
		if (!usdgRoom || ((await allowanceOf(payer, tokenAddress, escrowAddress)) ?? 0n) < amount) {
			render(t("Allowing the exact amount…"));
			const approvalHash = await payer.submit({ to: tokenAddress, data: encodeFunctionData({ abi: tokenAbi, functionName: "approve", args: [escrowAddress, amount] }) });
			render(t("Approval submitted. Waiting for confirmation…"));
			await sentAndDone(payer, approvalHash, async () => ((await allowanceOf(payer, tokenAddress, escrowAddress)) ?? 0n) >= amount);
			stillPlayer(player);
		}
		// the request read again right before its payment, in every room (the approval can take minutes): one the server
		// has cancelled meanwhile (its round cancelled) or that ran out, or whose ticket runs out within moments, is never
		// paid. The vault refuses a late ticket after its fee; a cancelled request's payment is only refunded
		const paying = await requestStillPayable(intentId, Math.min(intentExpiresAt, Number(execution.authorization.deadline) * 1000), usdgRoom);
		stillPlayer(player);
		// paid already after all (in another tab, or found in the vault): nothing more is sent, the server puts them in
		if (paying === "paid") return;
		render(t("Paying the entry…"));
		const enter = {
			// (the vault checked against the ticket, and allowed to take the amount)
			to: escrowAddress,
			data: encodeFunctionData({
				abi: escrowAbi,
				functionName: "enter",
				args: [
					{
						...execution.authorization,
						roomConfigVersion: BigInt(execution.authorization.roomConfigVersion),
						amount: BigInt(execution.authorization.amount),
						deadline: BigInt(execution.authorization.deadline),
					},
					signature as `0x${string}`,
				],
			}),
		} as const;
		try {
			entryTransactionHash = await payer.submit(enter);
		} catch (error) {
			// it may have gone out all the same (an answer lost on the way): the vault says, and nothing is sent twice
			if (await paidAlready(paying.id, 4_000)) return;
			throw error;
		}
		// (a send that gave back no hash to follow is remembered as sent: a press after a reload checks the vault, and never
		// pays a second time)
		if (!traceable(entryTransactionHash)) entryTransactionHash = "sent";
		try {
			localStorage.setItem(transactionStorageKey, entryTransactionHash);
		} catch {
			/* storage unavailable */
		}
	}
	render(t("Entry submitted. Waiting for on-chain confirmation…"));
	try {
		// in a block and through, or (what decides) the vault holding it: the server then puts the player in
		await sentAndDone(payer, entryTransactionHash, () => paidAlready(intentId));
	} catch (error) {
		// the payment failed on the blockchain: that transaction is forgotten, so the next press doesn't wait on it for
		// ever; the vault says whether this request was paid all the same. (Only a transaction the chain refused: one
		// the wallet couldn't ask about this time stays remembered, or the next press paid a second time)
		if ((error as { readonly reverted?: boolean }).reverted === true) {
			try {
				localStorage.removeItem(transactionStorageKey);
			} catch {
				/* storage unavailable */
			}
			if (await paidAlready(intentId)) return;
		}
		throw error;
	}
	// seen in the vault (the server put the player in, or will at the next look): nothing more to confirm
	if (entryIntent?.id === intentId && (entryIntent.state === "consumed" || entryIntent.state === "confirmed")) return;
	if (!traceable(entryTransactionHash)) {
		if (await paidAlready(intentId)) return;
		throw new Error(t("Your payment was sent and isn't in the vault yet: wait a minute, then press Check my payment (nothing is sent twice)."));
	}
	const followedHash = entryTransactionHash;
	const confirm = () =>
		request<EntryPaymentIntentOwnerViewV1>(
			`/entry/intents/${encodeURIComponent(intentId)}/confirm`,
			{
				method: "POST",
				body: JSON.stringify({ transactionHash: followedHash }),
			},
		);
	try {
		entryIntent = await confirm();
	} catch (error) {
		// the server couldn't take this transaction as the payment (one carrying several players' payments, say): the
		// vault itself says whether this request is paid
		if (await paidAlready(intentId)) return;
		throw error;
	}
	// the server waits for enough blocks on top of the payment: ask again for a minute (it puts the payer in the round
	// as soon as it's confirmed; if this page closes, the server still finds the payment before the round starts)
	for (let attempt = 0; attempt < 20 && entryIntent.state === "confirming"; attempt++) {
		render(t("Payment received. Waiting for the blockchain to confirm it…"));
		await new Promise((resolve) => setTimeout(resolve, 3_000));
		entryIntent = await confirm();
	}
};

/** older servers didn't stamp tickets: the paying wallet signed it (a wallet app only) */
const signTicketInWallet = async (
	address: `0x${string}`,
	execution: NonNullable<EntryPaymentIntentOwnerViewV1["execution"]>,
): Promise<`0x${string}`> => {
	const provider = await walletProvider();
	return provider.request<`0x${string}`>({
		method: "eth_signTypedData_v4",
		params: [
			address,
			JSON.stringify({
				domain: execution.domain,
				primaryType: "EntryAuthorization",
				types: {
					EIP712Domain: [
						{ name: "name", type: "string" },
						{ name: "version", type: "string" },
						{ name: "chainId", type: "uint256" },
						{ name: "verifyingContract", type: "address" },
					],
					EntryAuthorization: [
						{ name: "intentId", type: "bytes32" },
						{ name: "payer", type: "address" },
						{ name: "roomId", type: "bytes32" },
						{ name: "roundId", type: "bytes32" },
						{ name: "roomConfigVersion", type: "uint64" },
						{ name: "amount", type: "uint256" },
						{ name: "deadline", type: "uint64" },
						{ name: "fundsPolicy", type: "uint8" },
					],
				},
				message: execution.authorization,
			}),
		],
	});
};

// ---------- a guest's winnings: it says where its pot goes, signed by the wallet that paid its entry
/**
 * why an address can't receive USDG here (undefined: it can): not an address, a mistyped one (mixed case carries a
 * checksum), a guest's made-up one or the zero address (nobody can receive there), the game's own coin or vault
 */
const payoutAddressProblem = (input: string): string | undefined => {
	const text = input.trim();
	if (!/^0x[0-9a-fA-F]{40}$/u.test(text)) return t("Enter an EVM address: 0x followed by 40 letters and digits");
	const body = text.slice(2);
	if (body !== body.toLowerCase() && body !== body.toUpperCase() && !isAddress(text, { strict: true }))
		return t("That address has a typo (its capital letters don't add up): copy it again");
	try {
		parsePayoutAddress(text);
	} catch (error) {
		// (the rule the server checks too: its words, in the language picked when the phrasebook has them)
		return error instanceof Error ? tr(error.message) : t("That address can't receive anything");
	}
	const lower = text.toLowerCase();
	const usdg = entryMethods?.usdg;
	if (usdg && (lower === usdg.tokenAddress.toLowerCase() || lower === usdg.escrowAddress?.toLowerCase()))
		return t("That's one of the game's own contracts: enter your wallet's address");
	return undefined;
};

/** the wallet that paid a winning entry signs where the pot goes (a wallet app) */
const payerToSign = (payer: `0x${string}`): Promise<Payer> => walletPayer(payer, t("the wallet that paid your entry"));

/** the win whose pot's address the guest can choose now (the oldest one waiting first) */
const choosableWin = (): GuestWin | undefined =>
	[...guestWins].reverse().find(({ state, payer, open }) => (state === "provisional" || state === "finalized") && payer !== undefined && open === true);

/**
 * a guest winner says where its pot from one round goes (any EVM address); `payTo` absent: the wallet that paid its
 * entry. The message names this guest and that round: it can't be used for another guest's win, or another round's
 */
const claimWinnings = async (payTo?: string): Promise<void> => {
	const win = choosableWin();
	if (!win?.payer || !session) throw new Error(t("No winnings are waiting for your choice"));
	const problem = payoutAddressProblem(payTo ?? win.payer);
	if (problem) throw new Error(problem);
	const address = (payTo ?? win.payer).trim().toLowerCase();
	const player = session.address;
	const payer = await payerToSign(win.payer);
	stillPlayer(player);
	const message = payoutClaimMessage({
		site: window.location.host,
		player,
		playerName: nameInMessages(),
		roundId: win.roundId,
		payTo: address as `0x${string}`,
		payer: win.payer,
		// the server's clock (see clockOffset): a message dated by a phone's wrong clock was refused
		signedAt: serverNow(),
	});
	render(t("Sign the message in your wallet: your winnings go to <address> (free: it moves no money)…", { address: shorten(address) }));
	const signature = await payer.sign(message);
	stillPlayer(player);
	const result = await request<{ readonly payTo: string; readonly paidBy: "bot" | "admin" }>("/payouts/claim", {
		method: "POST",
		body: JSON.stringify({ payTo: address, message, signature }),
	});
	payoutFormOpen = false;
	clearAddressInputs();
	await refresh();
	renderNotice(
		result.paidBy === "bot"
			? t("Your winnings go to <address>: sent by the game once the result is final.", { address: shorten(result.payTo) })
			: t("Your winnings go to <address>: the admin sends them once the result is final.", { address: shorten(result.payTo) }),
	);
};

/** the address form under a guest's win, opened to choose or change where it goes */
let payoutFormOpen = false;

/** the winnings panel: a win and what became of it, a guest's win and where it goes */
const renderPayouts = (): void => {
	const panel = document.getElementById("payout-panel");
	const text = document.getElementById("payout-text");
	const form = document.getElementById("payout-form");
	const keep = document.getElementById("btn-payout-keep");
	const change = document.getElementById("btn-payout-change");
	if (!panel || !text || !form || !keep || !change) return;
	const decimals = entryMethods?.usdg?.tokenDecimals ?? 6;
	const room = (roomId: RoomId) => trMaybe(roundStatus?.rooms.find(({ definition }) => definition.id === roomId)?.definition.displayName) ?? roomId;
	// the win waiting for a choice first (the oldest), then one waiting to be paid, then one closed in the last day
	const pending = session?.guest ? (choosableWin() ?? guestWins.find(({ state }) => state === "provisional" || state === "finalized")) : undefined;
	const closed = session?.guest ? guestWins.find(({ state, endedAt }) => (state === "paid" || state === "refunded") && Date.now() - (endedAt ?? 0) < 24 * 60 * 60_000) : undefined;
	const win = pending ?? closed;
	const pot = (atomic?: string) => (atomic ? `${exactCoinText(atomic, decimals)} USDG` : t("the pot"));
	/**
	 * what the winner gets: the pot less the vault's betting fee, said once ("95 USDG (the 100 USDG pot less 5%)"); while
	 * the vault's fee isn't known (not read from it yet), the pot "less the vault's betting fee, if it takes one": never
	 * the whole pot as if it took none
	 */
	const prize = (win: { readonly potAtomic?: string; readonly prizeAtomic?: string; readonly winnerFeeBps?: number }) => {
		if (win.prizeAtomic === undefined) return win.potAtomic ? t("the <pot> pot (less the vault's betting fee, if it takes one)", { pot: pot(win.potAtomic) }) : t("the pot");
		return win.winnerFeeBps && win.potAtomic
			? t("<prize> (the <pot> pot less <fee>)", { prize: pot(win.prizeAtomic), pot: pot(win.potAtomic), fee: feePercentText(win.winnerFeeBps) })
			: pot(win.prizeAtomic);
	};
	/** what was (or is being) paid: the prize, or "your winnings" while it isn't known */
	const paid = (win: { readonly prizeAtomic?: string }) => (win.prizeAtomic ? pot(win.prizeAtomic) : t("your winnings"));
	// a player signed in with their wallet: their win's pot goes to that wallet, nothing to choose (said how it's doing)
	const own = session && !session.guest
		? (walletWins.find(({ state }) => state === "provisional" || state === "finalized") ??
			walletWins.find(({ state, endedAt }) => (state === "paid" || state === "refunded") && Date.now() - (endedAt ?? 0) < 24 * 60 * 60_000))
		: undefined;
	let shown = false;
	if (own) {
		shown = true;
		text.textContent =
			own.state === "paid"
				? `✓ ${t("Paid <amount> to your wallet (your win in the <room> room).", { amount: paid(own), room: room(own.roomId) })}`
				: own.state === "refunded"
					? t("Your win in the <room> room wasn't paid: an admin refunded every entry of that round instead (yours went back to the wallet that paid it).", { room: room(own.roomId) })
					: `🏆 ${sentences(
							t("You won <prize> in the <room> room!", { prize: prize(own), room: room(own.roomId) }),
							own.state === "provisional"
								? t("The result becomes final <when>, then it's sent to your wallet.", { when: finalWhen(own.finalizesAt) })
								: t("The result is final: it's on its way to your wallet."),
						)}`;
	} else if (!win) text.textContent = "";
	else {
		shown = true;
		if (win.state === "paid")
			text.textContent = `✓ ${
				win.payTo
					? t("Paid <amount> to <address> (your win in the <room> room).", { amount: paid(win), address: shorten(win.payTo), room: room(win.roomId) })
					: t("Paid <amount> to you (your win in the <room> room).", { amount: paid(win), room: room(win.roomId) })
			}`;
		else if (win.state === "refunded")
			text.textContent = win.payer
				? t("Your win in the <room> room wasn't paid: an admin refunded every entry of that round instead (yours went back to the wallet that paid it, <wallet>).", { wallet: shorten(win.payer), room: room(win.roomId) })
				: t("Your win in the <room> room wasn't paid: an admin refunded every entry of that round instead (yours went back to the wallet that paid it).", { room: room(win.roomId) });
		else if (!win.payTo) {
			const until = win.claimUntil ? new Date(win.claimUntil).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : undefined;
			text.textContent = `🏆 ${sentences(
				t("You won <prize> in the <room> room!", { prize: prize(win), room: room(win.roomId) }),
				t("Where should it go? Any EVM wallet address works (MetaMask, Coinbase Wallet, an exchange address that takes USDG on Robinhood Chain…)."),
				until
					? win.payer
						? t("If you don't choose by <time>, it goes to the wallet that paid your entry (<wallet>).", { time: until, wallet: shorten(win.payer) })
						: t("If you don't choose by <time>, it goes to the wallet that paid your entry.", { time: until })
					: undefined,
			)}`;
		} else {
			// (the wallet that paid the entry, said so when it wasn't chosen: it's where the pot goes once the wait is over)
			const destination = win.chosen ? shorten(win.payTo) : t("<address> (the wallet that paid your entry)", { address: shorten(win.payTo) });
			text.textContent = `🏆 ${
				win.prizeAtomic
					? win.state === "provisional"
						? t("Your <amount> goes to <destination>: sent once the result is final (<when>).", { amount: pot(win.prizeAtomic), destination, when: finalWhen(win.finalizesAt) })
						: t("Your <amount> goes to <destination>: on its way.", { amount: pot(win.prizeAtomic), destination })
					: win.state === "provisional"
						? t("Your winnings go to <destination>: sent once the result is final (<when>).", { destination, when: finalWhen(win.finalizesAt) })
						: t("Your winnings go to <destination>: on its way.", { destination })
			}`;
		}
	}
	// where it goes can be chosen while the result is under review, or once it's final until the first choice (then
	// it's fixed: the pot may be on its way)
	const changeable = pending !== undefined && pending.open === true && pending.payer !== undefined;
	const choosing = changeable && (!pending?.payTo || payoutFormOpen);
	form.hidden = !choosing;
	keep.hidden = !choosing || !pending?.payer;
	if (pending?.payer) keep.textContent = t("Keep it in the wallet that paid (<wallet>)", { wallet: shorten(pending.payer) });
	change.hidden = !(changeable && pending?.payTo && !payoutFormOpen);
	if (!changeable) payoutFormOpen = false;
	panel.hidden = !shown;
};

const act = async (): Promise<void> => {
	if (autoJoin) {
		autoJoinHandled.add(autoJoin.roundId);
		autoJoin = undefined;
		renderNotice(t("Auto-join cancelled. Press Enter battle below when you're ready."));
		return;
	}
	if (!session) return playAsGuest();
	if (registration) {
		const seat = registration;
		// a paid seat (by its room, even before the room list has loaded: leaving one used to skip this question) can be
		// given up only by the only player in the round: it's cancelled, and their entry refunded
		if (roomIsPaid(seat.roomId)) {
			const roomState = roundStatus?.rooms.find(({ definition }) => definition.id === seat.roomId);
			const which = roomState ? t("the <room> round", { room: tr(roomState.definition.displayName) }) : t("this paid round");
			const yours = roomState?.entryLabel ? t("your <price>", { price: tr(roomState.entryLabel) }) : t("your entry");
			// (the same, starting a sentence)
			const Yours = roomState?.entryLabel ? t("Your <price>", { price: tr(roomState.entryLabel) }) : t("Your entry");
			// a guest's entry goes back to the wallet that paid it (paid in another tab: the server's word on the last
			// payment)
			const paidFrom = [entryIntent, latestPaidEntry].find((intent) => intent?.roundId === seat.roundId)?.payer;
			const back = paidFrom ? t("the wallet that paid it (<wallet>)", { wallet: shorten(paidFrom) }) : session.guest ? t("the wallet that paid it") : t("your wallet");
			if (!window.confirm(t("Leave <round>? You're the only one in it, so it's cancelled and <entry> goes back to <wallet>.", { round: which, entry: yours, wallet: back }))) return;
			authEpoch++;
			const left = await request<{ readonly removed?: boolean; readonly refunded?: boolean; readonly refundBy?: "bot" | "admin" }>("/rounds/registration", {
				method: "DELETE",
			});
			await refresh();
			// out of the round, and its entry refunded: said only when the answer says both
			if (left.removed === true && left.refunded === true) {
				renderNotice(
					left.refundBy === "admin"
						? t("You left, and the round was cancelled. <entry> goes back to <wallet> once the admin sends the refund.", { entry: Yours, wallet: back })
						: t("You left, and the round was cancelled. <entry> is on its way back to <wallet> (usually within a minute or two).", { entry: Yours, wallet: back }),
				);
				return;
			}
			// not taken out: the round started meanwhile (an admin's Start now, say), so the seat and its entry stay in it
			// (they were told they had left while their entry was in a live round). The session checks show the rest
			renderNotice(
				registration?.roundId === seat.roundId
					? t("You're still in <round>: it's starting, so it can't be left any more and <entry> stays in it. Get ready to play.", { round: which, entry: yours })
					: t("Your seat had already moved on (the round started, or was cancelled): the menu shows where your entry stands now."),
			);
			return;
		}
		authEpoch++;
		await request("/rounds/registration", { method: "DELETE" });
	} else {
		const room = roundStatus?.rooms.find(
			({ definition }) => definition.id === selectedRoomId,
		)?.definition;
		if (!room) throw new Error(t("The selected room is unavailable"));
		if (room.entryPolicy.kind !== "free") {
			/** what the player said yes to at this press (a USDG room's price, from the wallet chosen) */
			let confirmed: PayConfirmed | undefined;
			// a guest pays the USDG rooms (from a wallet app); the Token room is for wallets signed in
			if (!session.verified && !(session.guest && isStakeRoom(room.id)))
				throw new Error(session.guest ? t("Guests play the USDG rooms: sign in with your wallet for this room") : t("Sign in with your wallet before paying to enter"));
			// a payment going through for another room is finished first (the menu keeps its room picked meanwhile)
			if (entryIntent && paymentUnderWay(entryIntent) && entryIntent.roomId !== room.id)
				throw new Error(t("A payment for another room is going through: finish it there first"));
			// a new request for the next payment (the finished one stays on screen if it's refused), and straight on
			// to paying it: one press, then the wallet. Not while a payment seen on the
			// blockchain is still being confirmed for its round, however long that takes: it's checked, not paid again
			if (!paymentGoingIn(entryIntent) && (!entryIntent || finishedIntent(entryIntent) || strayEntry(entryIntent))) {
				const roomState = roundStatus?.rooms.find(({ definition }) => definition.id === room.id);
				// what the server would refuse anyway, said before a wallet is asked anything
				if (session.guest && session.expiresAt - serverNow() < 2 * 24 * 60 * 60_000)
					throw new Error(t("Your guest sign-in ends in less than two days (a guest lasts a month), before this round and its payout would be done: sign out and play as a new guest to pay. Nothing was charged."));
				if ((roomState?.nextRound?.registeredCount ?? 0) >= limitsOf(roomState?.definition).maxPlayers)
					throw new Error(t("Every seat in this round is taken: you can pay for the next round once it's played. Nothing was charged."));
				// an earlier payment from this browser the game lost (a restart): it's refunded, and paying again is a second
				// payment, so it's asked first (never paid again unawares)
				const lost = lostPaymentFor(room.id);
				if (lost && lost.acknowledged !== true) {
					if (!window.confirm(`${lostPaymentText(lost)}\n\n${t("Pay again now to join this round? That's a new payment: the earlier one is still refunded.")}`)) {
						renderNotice(t("Nothing was charged. Your earlier payment is refunded to the wallet that paid it, by the game's settlement bot (usually within about 2 hours) or by an admin."));
						return;
					}
					noteLostPayment({ ...lost, acknowledged: true });
				}
				const payer = await choosePayer(room.id);
				if (!payer) {
					renderNotice(t("Nothing was charged."));
					return;
				}
				activePayer = payer;
				// a USDG room: the wallet on the right network, and the price said yes to, before the payment request is
				// made. A request holds the player's seat and wallet until it runs out (its ticket is good in the vault until
				// then): saying no here, or to the network switch, used to keep the menu waiting on it for minutes
				const usdg = entryMethods?.usdg;
				if (isStakeRoom(room.id) && usdg && room.entryPolicy.kind === "fixed_token" && room.entryPolicy.amountAtomic) {
					await payer.prepare(usdg.chainId);
					stillPlayer(session.address);
					const unit = labelCoin(roomState?.entryLabel) ?? "USDG";
					const price = `${exactCoinText(room.entryPolicy.amountAtomic, usdg.tokenDecimals)} ${unit}`;
					if (!window.confirm(payQuestion({ price, payer, roomId: room.id, ticketSigned: true }))) {
						renderNotice(t("Nothing was charged."));
						return;
					}
					confirmed = { amountAtomic: room.entryPolicy.amountAtomic, payer: payer.address };
				}
				// the room chosen at the press (the selector is fixed meanwhile)
				entryIntent = await requestPayment(room.id, payer);
				// (one that can't be paid from here, or is already over, is only shown)
				if (!entryIntent.execution || finishedIntent(entryIntent)) return;
			}
			// paid already, being confirmed: nothing is sent again (the vault would refuse it, after a fee and an
			// approval); the server is asked to look in the vault, and puts the player in the round if it's there
			if (entryIntent.state === "confirming") {
				render(t("Checking your payment in the vault…"));
				if (!(await paidAlready(entryIntent.id))) {
					renderNotice(t("Your payment is in, and the blockchain is still confirming it: your seat is kept once it is (the game keeps checking, even if you close this page)."));
					return;
				}
			}
			// in the round already (the server puts a paying player in as soon as it's confirmed)
			if (entryIntent.state === "consumed") {
				await refresh();
				return;
			}
			if (entryIntent.state !== "confirmed") {
				if (entryIntent.execution) await executeLivePaidEntry(await payerForIntent(entryIntent), confirmed);
				else {
					entryIntent = await request<EntryPaymentIntentOwnerViewV1>(
						`/entry/intents/${encodeURIComponent(entryIntent.id)}`,
					);
				}
				// paying is joining: the server puts them in the round as soon as the payment is confirmed
				if (entryIntent.state === "consumed") {
					await refresh();
					return;
				}
				if (entryIntent.state !== "confirmed") return;
			}
		}
		authEpoch++;
		await request("/rounds/registration", {
			method: "POST",
			// the room this press was for (the one paid for, in a paid room), whatever the menu shows by now
			body: JSON.stringify({ roomId: room.id }),
		});
	}
	await refresh();
};

/** signing out on this browser (a guest is asked first: it can't come back, it has no wallet to sign in with) */
/** a guest has paid for a round (a seat, a round being played, or a payment on its way): it must stay signed in */
const guestHasPaidPlace = (): boolean =>
	session?.guest === true &&
	((registration !== null && roomIsPaid(registration.roomId) && (registration.state !== "playing" || registration.knockedOut === undefined)) ||
		(paymentUnderWay(entryIntent) && entryIntent?.state !== "consumed"));
const guestPaidPlace = (): string =>
	t("Your guest has a paid seat, a paid round being played, or a payment going through: finish that first. Signing out (or signing in with a wallet here) now would leave your seat with nobody who can play it.");

/**
 * What paying through Privy (removed in oct18a) left in this browser: the card wallet it remembered, and Privy's own
 * sign-in in its storage and cookies. Forgotten once, when the menu starts.
 */
const forgetOldCardWallet = (): void => {
	try {
		for (const key of ["trenchio.cardWallet", "trenchio.cardFundedAt", "trenchio.cardEthKept"]) localStorage.removeItem(key);
		for (const key of Object.keys(localStorage)) if (key.startsWith("privy:")) localStorage.removeItem(key);
	} catch {
		/* storage unavailable */
	}
	for (const name of ["privy-token", "privy-id-token", "privy-session", "privy-refresh-token"])
		if (document.cookie.includes(`${name}=`)) document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
};

/**
 * A guest's win whose payout address it can still choose, and hasn't: only this guest can choose it (its guest ends
 * when it signs out, or signs in with a wallet), so that's said first. False when the player keeps their guest.
 */
const giveUpUnchosenWin = (ending: string, question: string): boolean => {
	const choosable = session?.guest ? guestWins.find(({ state, open, payTo }) => state !== "paid" && state !== "refunded" && open !== false && !payTo) : undefined;
	if (!choosable) return true;
	const usdg = (atomic: string) => `${exactCoinText(atomic, entryMethods?.usdg?.tokenDecimals ?? 6)} USDG`;
	// what the vault pays of the pot (less its betting fee); while that isn't known, the pot less the fee, if any
	const won = choosable.prizeAtomic
		? usdg(choosable.prizeAtomic)
		: choosable.potAtomic
			? t("the <pot> pot (less the vault's betting fee, if it takes one)", { pot: usdg(choosable.potAtomic) })
			: t("a pot");
	// where it goes then, and from when (`ending` starts that sentence: "Signed out")
	const wallet = choosable.payer ? t("the wallet that paid your entry (<wallet>)", { wallet: shorten(choosable.payer) }) : t("the wallet that paid your entry");
	const after = choosable.claimUntil ? new Date(choosable.claimUntil).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : undefined;
	return window.confirm(
		sentences(
			t("You won <prize> and haven't said where it goes.", { prize: won }),
			after
				? t("<ending>, you can't choose any more: it goes to <wallet> after <time>.", { ending, wallet, time: after })
				: t("<ending>, you can't choose any more: it goes to <wallet>.", { ending, wallet }),
			question,
		),
	);
};

/**
 * Signing out on a shared computer: a wallet app connected through WalletConnect (a phone's) is disconnected here, so
 * the next person on this browser isn't offered it; and the payments' transactions this browser remembered go too. (A
 * wallet extension is the browser's own: nothing to end)
 */
const forgetWalletConnection = async (): Promise<void> => {
	const connected = walletConnectProvider;
	walletConnectProvider = undefined;
	if (connected) await connected.disconnect().catch(() => undefined);
	await clearWalletConnectStorage();
	// (and what was said of a payment the game lost: it's still refunded to the wallet that paid it)
	lostPaymentList = [];
	try {
		// (older WalletConnect versions kept their connections here)
		for (const key of Object.keys(localStorage))
			if (key.startsWith("wc@2:") || key.startsWith("trenchio.entryTransaction.") || key === LOST_PAYMENTS_KEY) localStorage.removeItem(key);
	} catch {
		/* storage unavailable */
	}
};

const signOut = async (): Promise<void> => {
	if (guestHasPaidPlace()) throw new Error(guestPaidPlace());
	if (!giveUpUnchosenWin(t("Signed out"), t("Sign out anyway?"))) return;
	// a place waiting in the free room's queue goes with the sign-in (the server gives it up: nobody would be there to
	// play it); a paid seat stays (its entry is in the vault); a running round goes on (away, you're out after 30 s)
	const queued = registration !== null && registration.state !== "playing";
	const paidSeat = queued && roomIsPaid(registration?.roomId);
	// (a wallet signed in on another device too keeps its place there; a paid seat stays, and so does the wallet's hold
	// on this connection until that round is over, so no other wallet signing in here can lock it out of it)
	const leaving = paidSeat
		? t("Your paid seat stays yours: sign in again with this wallet to play it.")
		: queued
			? session?.guest
				? t("Your place in the queue goes too.")
				: t("Your place in the queue goes too, unless this wallet is signed in on another device.")
			: inRoundToPlay()
				? t("You're in a running round: you can't come back to it.")
				: "";
	const question = session?.guest
		? sentences(t("Sign out of <name>? A guest can't come back: next time you play as a new guest, starting from nothing.", { name: myDisplayName() ?? t("this guest") }), leaving)
		: leaving
			? sentences(t("Sign out?"), leaving)
			: undefined;
	if (question && !window.confirm(question)) return;
	authEpoch++;
	await request("/auth/logout", { method: "POST" });
	await forgetWalletConnection();
	// a session check sent while signing out may come back after it, with the old sign-in: it's from before now
	authEpoch++;
	session = undefined;
	noteSignedIn(undefined);
	// what was said while signed in (a sign-in message, an error), the room picked, a payment: none of it stands now
	resetPlayerState();
	untickTerms();
	render();
};

const startDevelopmentTest = async (): Promise<void> => {
	if (!session)
		throw new Error(t("Play as a guest or sign in with your wallet before starting a test battle"));
	if (!developmentTestMode)
		throw new Error(t("Test battles are available only in development mode"));
	if (registration?.state === "playing")
		throw new Error(t("You are playing in the running public round"));
	const countInput = document.getElementById(
		"dev-test-bot-count",
	) as HTMLInputElement | null;
	const botCount = Number(countInput?.value ?? 3);
	// (the weather chosen for Quick start, 1.05)
	const weather = (document.getElementById("dev-sky") as HTMLSelectElement | null)?.value ?? "random";
	developmentAdmission = await request<DevelopmentAdmission>(
		"/dev/test-match",
		{
			method: "POST",
			body: JSON.stringify({ botCount, weather }),
		},
	);
	render(
		t("Launching private test battle against <n> bots…", { n: developmentAdmission.botCount }),
	);
	document.getElementById("btn-play-solo")?.click();
};

const spectateDevelopmentTest = async (): Promise<void> => {
	if (!session)
		throw new Error(t("Play as a guest or sign in with your wallet before spectating a test battle"));
	if (!activeDevelopmentTest)
		throw new Error(t("There is no active test battle to spectate"));
	developmentAdmission = await request<DevelopmentAdmission>(
		"/dev/test-match/spectate",
		{ method: "POST" },
	);
	render(
		t("Opening spectator view for the <n>-bot battle…", { n: developmentAdmission.botCount }),
	);
	document.getElementById("btn-play-solo")?.click();
};

export const isWalletAdmitted = (): boolean =>
	inRoundToPlay() || developmentAdmission !== undefined;

/** the signed-in player as the store sees it (0.92): its address, and whether it's a guest or a wallet that signed in */
export const storeBuyer = (): { readonly address: string; readonly guest: boolean; readonly verified: boolean } | undefined =>
	session ? { address: session.address, guest: session.guest === true, verified: session.verified === true } : undefined;


/**
 * A store item paid (0.92): the player's own wallet burns the price, in the game's coin, on the coin's chain (0.95: the
 * coin's own burn; the store saying "transfer", or nothing (a server from before 0.95), it's sent to the dead address, as
 * until 0.94). The transaction's hash once the wallet has sent it: the server finds the burn from there
 */
export const sendStorePayment = async (
	pay: Pick<StorePayInstruction, "token" | "chainId" | "to" | "amountAtomic"> & Partial<Pick<StorePayInstruction, "method" | "decimals" | "symbol">>,
	say: (text: string) => void,
): Promise<string> => {
	// (an error before the wallet is asked to send: `notSent`, nothing went out; one after it, the payment may have gone
	// out all the same (a wallet that sent it, then lost its answer): the store's card only checks for it a while. A
	// wallet's plain object made an error in its own words, its code kept (0.93 review: never "[object Object]")
	const asError = (error: unknown): Error =>
		error instanceof Error ? error : Object.assign(new Error(wordsOf(error)), { code: (error as { readonly code?: unknown } | undefined)?.code });
	const notSent = (error: unknown): Error => Object.assign(asError(error), { notSent: true });
	const cancelled = (): Error => Object.assign(new Error(t("You cancelled it in your wallet: nothing was paid.")), { cancelled: true, notSent: true });
	let payer: Payer;
	let data: `0x${string}`;
	// (burned with the coin's own burn: it has no address to send to)
	const burns = pay.method === "burn";
	try {
		if (!session || session.guest) throw new Error(t("Sign in with your wallet first"));
		if (!isAddress(pay.token) || (!burns && !isAddress(pay.to)) || !/^\d{1,78}$/u.test(pay.amountAtomic)) throw new Error(t("The store's answer isn't a payment: nothing was sent"));
		payer = await walletPayer(session.address, t("the wallet you signed in with, which gets the item"), { what: t("the payment"), notify: say });
		say(t("Your wallet may ask to switch network first…"));
		await payer.prepare(pay.chainId);
		// (what the wallet shows is a call of the coin's burn, its amount in the coin's smallest units: said in tokens here)
		const price = pay.decimals !== undefined && pay.symbol ? `${formatTokenAmount(pay.amountAtomic, pay.decimals)} ${pay.symbol}` : undefined;
		say(
			burns
				? price === undefined
					? t("Confirm in your wallet: it burns the price…")
					: t("Confirm in your wallet: it burns <price>…", { price })
				: t("Confirm the payment in your wallet…"),
		);
		data = burns
			? encodeFunctionData({ abi: storeBurnAbi, functionName: "burn", args: [BigInt(pay.amountAtomic)] })
			: encodeFunctionData({ abi: storeTokenAbi, functionName: "transfer", args: [getAddress(pay.to), BigInt(pay.amountAtomic)] });
	} catch (error) {
		// (refused in the wallet as it connected or switched network, however it says it: nothing was sent)
		if (refusedInWallet(error)) throw cancelled();
		throw notSent(error);
	}
	try {
		return await payer.submit({ to: getAddress(pay.token), data });
	} catch (error) {
		// (refused in the wallet, however it says it: nothing was sent, and the store's card can offer to pay it after all)
		if (refusedInWallet(error)) throw cancelled();
		// (the network checked again before sending, and found another: nothing was sent. Known by its kind: its words are
		// the player's language's)
		if (error instanceof NothingSent || (error instanceof Error && /Nothing was sent\.$/u.test(error.message))) throw notSent(error);
		throw asError(error);
	}
};

/** the Training Range: anyone gets a private practice range, no wallet or round needed */
export const requestTrainingTicket = (): Promise<{
	readonly gameID: number;
	readonly ticket: string;
}> => request("/training", { method: "POST" });

export const requestWalletGameTicket = (): Promise<{
	readonly gameID: number;
	readonly ticket: string;
}> => {
	if (developmentAdmission) {
		const admission = developmentAdmission;
		developmentAdmission = undefined;
		return Promise.resolve(admission);
	}
	return request("/game-ticket", { method: "POST" });
};

export const requestWalletReconnectTicket = (): Promise<{
	readonly gameID: number;
	readonly ticket: string;
}> => request("/reconnect-ticket", { method: "POST" });

/**
 * the focus back on a button after its press, when the press took it away: a button is disabled while its press is
 * under way (or hidden after it, as Sign in with wallet once signed in), and the browser then drops the focus to the
 * page, so a keyboard player had to tab from the top again. Back on the first of `buttons` that can take it, unless
 * the player has moved on meanwhile
 */
const focusBack = (hadFocus: boolean, ...buttons: ReadonlyArray<HTMLElement | null>): void => {
	if (!hadFocus || (document.activeElement && document.activeElement !== document.body)) return;
	const button = buttons.find(
		(candidate) =>
			candidate?.isConnected === true &&
			!(candidate as HTMLButtonElement).disabled &&
			candidate.closest("[hidden]") === null &&
			candidate.getClientRects().length > 0,
	);
	button?.focus({ preventScroll: true });
};

export async function initWalletFlow(): Promise<void> {
	// (its first words in the player's language: the language comes in with the game, in a moment; 1.04.4)
	await Promise.race([translationsReady, new Promise<void>(resolve => setTimeout(resolve, 3000))]);
	// the address the retired address box remembered: not read any more, and not left on a shared computer
	try {
		localStorage.removeItem("trenches_last_address");
	} catch {
		/* storage unavailable */
	}
	// and what the retired card payments (Privy) kept here
	forgetOldCardWallet();
	const mainButton = document.getElementById("btn-wallet-action");
	mainButton?.addEventListener("click", () => {
			// this click is a user gesture: the sounds below may start the audio here
			try {
				audioCtx ??= new AudioContext();
				void audioCtx.resume();
			} catch {
				/* no audio */
			}
			// one press, one request: a second press while the first is on its way does nothing (a double click made
			// two guests, or answered "already in the queue")
			if (actionBusy()) return;
			const pressedAt = Date.now();
			actionInFlightSince = pressedAt;
			const hadFocus = document.activeElement === mainButton;
			const wasRegistered = Boolean(registration);
			// a new try: an earlier error no longer stands on the panel (a new one replaces it if this fails too)
			stickyError = undefined;
			render(session ? t("Updating your place…") : t("Starting your guest session…"));
			void act()
				.then(() => {
					if (!wasRegistered && registration) playReadyChime();
				}) // joined the round: chime
				.catch((error: unknown) =>
					renderError(
						// a refusal in the wallet, however the wallet says it; its "a request is already open"; else its own words
						// (the server's, in the language picked when the phrasebook has them)
						refusedInWallet(error)
							? cancelledInWalletText()
							: walletCodesOf(error).includes(-32002)
								? t("Your wallet already has a request waiting: open it (MetaMask: the fox in the browser's toolbar) and finish or cancel it, then try again.")
								: error instanceof Error || typeof (error as { readonly message?: unknown } | undefined)?.message === "string"
									? tr(wordsOf(error))
									: t("Account action failed"),
					),
				)
				.finally(() => {
					if (actionInFlightSince === pressedAt) {
						actionInFlightSince = undefined;
						flowStatus = undefined;
					}
					render();
					focusBack(hadFocus, mainButton);
				});
		});
	document
		.getElementById("btn-wallet-change")
		?.addEventListener("click", () => {
			if (actionBusy() || walletPending) return;
			stickyError = undefined;
			void signOut().catch((error) =>
				renderError(
					error instanceof Error ? tr(error.message) : t("Couldn't sign out"),
				),
			);
		});
	// signing in with the wallet itself (from signed out, or a guest moving to their wallet)
	document
		.getElementById("btn-wallet-verify")
		?.addEventListener("click", (event) => {
			// while the wallet is asked, this button is Cancel (not in the first moment: the second click of a double
			// click on Sign in with wallet would cancel it at once)
			if (walletPending) {
				if (walletStep !== "check" && Date.now() - walletPending.since > 600) cancelWalletSignIn();
				return;
			}
			if (actionBusy()) return;
			stickyError = undefined;
			const verifyButton = event.currentTarget as HTMLElement;
			const hadFocus = document.activeElement === verifyButton;
			void signInWithWallet()
				.catch((error) =>
					renderError(
						error instanceof Error
							? tr(error.message)
							: t("Signing in with your wallet didn't work: try again"),
					),
				)
				// (signed in, the button is gone: the main button takes the focus)
				.finally(() => focusBack(hadFocus, verifyButton, mainButton));
		});
	document.getElementById("btn-dev-test")?.addEventListener("click", () => {
		render(t("Preparing an isolated development battle…"));
		void startDevelopmentTest().catch((error) =>
			renderError(
				error instanceof Error ? tr(error.message) : t("Unable to start test battle"),
			),
		);
	});
	document.getElementById("btn-dev-spectate")?.addEventListener("click", () => {
		render(t("Preparing the spectator view…"));
		void spectateDevelopmentTest().catch((error) =>
			renderError(
				error instanceof Error
					? tr(error.message)
					: t("Unable to spectate test battle"),
			),
		);
	});
	document
		.getElementById("room-selector")
		?.addEventListener("change", (event) => {
			selectedRoomId = (event.currentTarget as HTMLSelectElement)
				.value as RoomId;
			entryIntent = undefined;
			void refreshLobby();
			render();
		});
	document
		.getElementById("btn-spectate-current")
		?.addEventListener("click", (event) => {
			event.preventDefault();
			event.stopPropagation(); // the page-wide click would close the dropdown again
			if ((event.currentTarget as HTMLElement).dataset.mode !== "list") return;
			void refreshGames();
			const list = document.getElementById("server-list");
			if (list && !list.classList.contains("active"))
				document.getElementById("btn-server-select")?.click();
			document
				.getElementById("server-select")
				?.scrollIntoView({ block: "nearest", behavior: "smooth" });
		});
	document.getElementById("server-list")?.addEventListener("click", (event) => {
		const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
			".gl-btn",
		);
		if (!button) return;
		event.stopPropagation();
		const act = button.dataset.act === "join" ? "join" : "watch";
		button.disabled = true;
		void openGame(act, Number(button.dataset.game))
			.catch((error) =>
				renderError(
					error instanceof Error ? tr(error.message) : t("Unable to open that game"),
				),
			)
			.finally(() => {
				button.disabled = false;
			});
	});
	document
		.getElementById("btn-server-select")
		?.addEventListener("click", () => {
			void refreshGames();
		});
	document
		.getElementById("btn-edit-name")
		?.addEventListener("click", openNameEditor);
	document
		.getElementById("btn-name-cancel")
		?.addEventListener("click", closeNameEditor);
	document.getElementById("btn-name-save")?.addEventListener("click", () => {
		void saveName();
	});
	document.getElementById("name-input")?.addEventListener("input", () => {
		nameEdited = true;
	});
	document
		.getElementById("name-input")
		?.addEventListener("keydown", (event) => {
			event.stopPropagation(); // typing a name must not trigger game key bindings
			if (event.key === "Enter") {
				event.preventDefault();
				void saveName();
			}
			if (event.key === "Escape") closeNameEditor();
		});
	document.getElementById("name-input")?.addEventListener("input", (event) => {
		const hint = document.getElementById("name-hint");
		const value = (event.currentTarget as HTMLInputElement).value;
		if (!hint) return;
		if (!value.trim()) {
			hint.textContent = nameHint();
			hint.classList.remove("bad");
			return;
		}
		const check = checkNickname(value);
		hint.textContent = check.ok
			? t("Looks good. Press Enter to save.")
			: nicknameProblem(check.error);
		hint.classList.toggle("bad", !check.ok);
	});
	// (remembered on this device; a browser that refuses this site's storage keeps nothing, and the panel still starts:
	// it used to stop here)
	const stored = (key: string): string | null => {
		try {
			return localStorage.getItem(key);
		} catch {
			return null;
		}
	};
	const store = (key: string, value: string): void => {
		try {
			localStorage.setItem(key, value);
		} catch {
			/* storage unavailable */
		}
	};
	// how the practice bots play (remembered on this device)
	const botLevel = document.getElementById(
		"dev-bot-level",
	) as HTMLSelectElement | null;
	if (botLevel) {
		botLevel.value = stored("trenches_practice_bots") ?? "normal";
		botLevel.addEventListener("change", () =>
			store("trenches_practice_bots", botLevel.value),
		);
	}
	// how many bots, and whether they act like real players (random wallets): remembered on this device
	const botCount = document.getElementById("dev-bot-count") as HTMLSelectElement | null;
	if (botCount) {
		botCount.value = stored("trenches_practice_bot_count") ?? "fill";
		botCount.addEventListener("change", () => store("trenches_practice_bot_count", botCount.value));
	}
	const botWallets = document.getElementById("dev-bot-wallets") as HTMLInputElement | null;
	if (botWallets) {
		botWallets.checked = stored("trenches_practice_bot_wallets") === "1";
		botWallets.addEventListener("change", () => store("trenches_practice_bot_wallets", botWallets.checked ? "1" : "0"));
	}
	// the sky a Quick start's match is played under (1.05: random, or a clear day, dusk, night, snow, a thunderstorm), remembered
	const devSky = document.getElementById("dev-sky") as HTMLSelectElement | null;
	if (devSky) {
		const remembered = stored("trenches_practice_sky");
		devSky.value = remembered !== null && isSkyName(remembered) ? remembered : "random";
		devSky.addEventListener("change", () => store("trenches_practice_sky", devSky.value));
	}
	document
		.getElementById("btn-dev-quickstart")
		?.addEventListener("click", () => {
			render(t("Starting the round\u2026"));
			void request<{ started: boolean }>("/dev/quick-start", {
				method: "POST",
				body: JSON.stringify({
					bots: botLevel?.value ?? "normal",
					count: botCount?.value ?? "fill",
					wallets: botWallets?.checked ?? false,
					weather: devSky?.value ?? "random",
				}),
			})
				.then(() => refresh())
				.catch((error) =>
					renderError(
						error instanceof Error
							? tr(error.message)
							: t("Unable to start the round"),
					),
				);
		});
	document
		.getElementById("wallet-panel")
		?.addEventListener("click", unlockAudio, { capture: true });
	document.addEventListener("keydown", unlockAudio, { once: true });
	document.addEventListener("visibilitychange", () => render());
	// a guest's winnings: where they go (any EVM address, or the wallet that paid), signed by the wallet that paid
	const payoutAddress = document.getElementById("payout-address") as HTMLInputElement | null;
	payoutAddress?.addEventListener("keydown", (event) => event.stopPropagation()); // typing must not trigger game keys
	const payoutAction = (run: () => Promise<void>) => () => {
		if (actionBusy()) return;
		const pressedAt = Date.now();
		actionInFlightSince = pressedAt;
		stickyError = undefined;
		void run()
			.catch((error: unknown) =>
				renderError(refusedInWallet(error) ? t("You cancelled in your wallet: nothing was changed.") : error instanceof Error ? tr(error.message) : t("That didn't work: try again")),
			)
			.finally(() => {
				if (actionInFlightSince === pressedAt) {
					actionInFlightSince = undefined;
					flowStatus = undefined;
				}
				render();
			});
	};
	document.getElementById("btn-payout-send")?.addEventListener("click", payoutAction(() => claimWinnings(payoutAddress?.value ?? "")));
	document.getElementById("btn-payout-keep")?.addEventListener("click", payoutAction(() => claimWinnings()));
	document.getElementById("btn-payout-change")?.addEventListener("click", () => {
		payoutFormOpen = true;
		render();
	});
	// how the USDG rooms can be paid here, and their prize (asked again every minute: an admin may open a USDG room,
	// or move it to a new vault)
	await refreshEntryMethods();
	setInterval(() => void refreshEntryMethods(), 60_000);
	await refresh();
	// the menu's timers pause while it isn't on screen (during a match): no requests or redraws behind the game.
	// They pick up again within a couple of seconds of returning to the menu.
	const menuShown = (): boolean => {
		if (document.hidden) return false;
		const menu = document.getElementById("splash-ui");
		if (!menu) return true;
		// hidden, or (mostly) faded out when a match starts
		const style = getComputedStyle(menu);
		return style.display !== "none" && Number(style.opacity) >= 0.5;
	};
	// except for a seat waiting for its round, or a payment going through: followed in a background tab, or from the
	// Training Range, so the countdown's title, the bugle and auto-join still come when the round starts (they waited
	// for the player to come back, who was a no-show by then, their entry in the pot)
	const followedOffScreen = (): boolean =>
		Boolean((registration && registration.state !== "playing") || autoJoin || paymentUnderWay(entryIntent));
	// every 2 seconds, one check at a time (a slow answer had them pile up, two more requests every 2 seconds). While
	// the server doesn't answer (an update going in), asked less often: after 2, 4, 8, then every 16 seconds, and every
	// 2 again once it answers
	let checkFailures = 0;
	let nextCheckAt = 0;
	const checkSession = oneAtATime(async () => {
		if (Date.now() < nextCheckAt) return;
		await refresh();
		checkFailures = sessionCheckFailingSince === undefined ? 0 : Math.min(checkFailures + 1, 4);
		nextCheckAt = checkFailures ? Date.now() + 2_000 * 2 ** (checkFailures - 1) - 1_000 : 0;
	});
	_refreshTimer ??= setInterval(() => {
		if (menuShown() || followedOffScreen()) checkSession();
	}, 2_000);
	_uiTimer ??= setInterval(() => {
		if (menuShown() || followedOffScreen()) tickLobby();
	}, 1_000);
	const listGames = oneAtATime(refreshGames);
	listGames();
	setInterval(() => {
		if (menuShown()) listGames();
	}, 3_000);
	const ping = oneAtATime(measurePing);
	ping();
	setInterval(() => {
		if (menuShown()) ping();
	}, 10_000);
}
